import { CSSProperties, FormEvent, MouseEvent as ReactMouseEvent, useCallback, useEffect, useState, useRef } from "react"
import QRCode from "qrcode"
import BancFileUploader from "./BancFileUploader"
import { analyticsClassLabels, appendAnalyticsChartFilter, effectiveAnalyticsClass, isExcludedFromOrdinaryAnalytics, TransactionAnalyticsClass } from "./bankAnalytics"
import { apiFetch, bankAuthorizationName, bankAuthorizationResult, consumeSuccessfulBankAuthorization, hasBankAuthorizationSession, pollBankAuthorizationStatus, recoverApiAuthorization, requestBankMobileAuthorizationUrl, saveBankAuthorizationName } from "../api"

interface Transaction {
	_id?: string,
	hash?: string,
	date: Date,
	order: number,
	ref: number,
	amount: number,
	balance: number,
	concept: string,
	description: string,
	category?: number,
	subcategory?: number,
	analyticsClass?: TransactionAnalyticsClass,
	analyticsClassSource?: 'manual' | 'system',
	suggestedAnalyticsClass?: Exclude<TransactionAnalyticsClass, 'normal' | 'ignored'>,
	suggestionReason?: string,
	suggestionConfidence?: number,
}

interface EmptyDateRange {
	from: Date,
	until: Date,
	days: number,
	note: string,
}

interface TransactionListBounds {
	dates?: {
		lower: string,
		upper: string,
	},
	amounts?: {
		lower: number,
		upper: number,
	},
}

type TransactionSorting = 'dateDesc' | 'dateAsc' | 'amount'
type TransactionTypeFilter = 'anything' | 'debit' | 'credit'
type AmountSectionId = 'income' | 'highestDebits' | 'averageDebits' | 'lowestDebits'
type ChartPeriod = 'semester' | 'year' | 'three-years'
type BulkCategorizationField = 'concept' | 'description'

interface BankFilterState {
	search: string,
	dateFrom: string,
	dateUntil: string,
	amountMin: string,
	amountMax: string,
	transactionType: TransactionTypeFilter,
	sorting: TransactionSorting,
	category: string,
	subcategory: string,
}

interface SavedBankFilter {
	id: string,
	name: string,
	filters: BankFilterState,
	createdAt: string,
	updatedAt: string,
}

interface AmountSection {
	id: AmountSectionId,
	title: string,
	transactionKeys: string[],
	transactions: Transaction[],
	totalCount: number,
	totalAmount: number,
	nextCursor?: TransactionListCursor,
}

interface TransactionListCursor {
	date: string,
	order: number,
	amount?: number,
}

interface AmountThresholds {
	highestDebits: number,
	averageDebits: number,
}

interface AmountSectionsResponse {
	bounds?: TransactionListBounds,
	amountThresholds: AmountThresholds,
	amountSections: {
		id: AmountSectionId,
		transactions: Transaction[],
		totalCount?: number,
		totalAmount?: number,
		nextCursor?: TransactionListCursor,
	}[],
}

interface MonthlyFlow {
	month: string,
	label: string,
	income: number,
	expenses: number,
	net: number,
}

interface BankChartBalancePoint {
	date: string,
	order?: number,
	balance: number,
	samples?: {
		year: number,
		balance: number,
	}[],
}

interface BankChartMonthlyFlow {
	month: string,
	income: number,
	expenses: number,
	net: number,
}

interface BankChartCategoryBreakdown {
	category?: number,
	subcategory?: number,
	amount: number,
	income: number,
	expenses: number,
	projectedExpenses?: number,
	historicalIncomeShare?: number,
	historicalExpensesShare?: number,
	historicalNetShare?: number,
	historicalNetAmount?: number,
	historicalSubcategoryIncomeShare?: number,
	historicalSubcategoryExpensesShare?: number,
	historicalSubcategoryNetShare?: number,
	historicalSubcategoryNetAmount?: number,
	count: number,
}

interface BankCharts {
	range?: {
		lower: string,
		upper: string,
	},
	balance: BankChartBalancePoint[],
	balanceAverage?: BankChartBalancePoint[],
	monthlyFlow: BankChartMonthlyFlow[],
	categoryBreakdown: BankChartCategoryBreakdown[],
}

interface BankPeriodComparison {
	current: number,
	baseline: number,
	delta: number,
	ratio?: number,
	sampleSize: number,
	referenceDate?: string,
}

interface BankDashboardSummary {
	latest?: {
		date: string,
		balance: number,
	},
	savingForecast?: {
		date: string,
		balance: number,
		income: number,
		projectedExpenses: number,
	},
	monthScore?: BankPeriodComparison,
	yearScore?: BankPeriodComparison,
}

interface BankConceptSummary {
	concept?: string,
	description?: string,
	normalizedConcept?: string,
	normalizedDescription?: string,
	count: number,
	income: number,
	expenses: number,
	net: number,
	firstDate?: string,
	lastDate?: string,
	categories: {
		category?: number,
		subcategory?: number,
		count: number,
	}[],
}

interface DailyBalancePoint {
	date: string,
	balance: number,
	samples?: {
		year: number,
		balance: number,
	}[],
}

interface BalanceTooltip {
	index: number,
	x: number,
	y: number,
}

interface AverageBalanceTooltip {
	index: number,
	x: number,
	y: number,
}

interface FlowTooltip {
	month: string,
	label: string,
	kind: 'income' | 'expenses',
	amount: number,
	x: number,
	y: number,
}

interface CategoryTooltip {
	label: string,
	amount: number,
	currentShare: number,
	monthlyAmount?: number,
	historicalShare?: number,
	historicalAmount?: number,
	x: number,
	y: number,
}

const formatDate = (date: Date): string => {
	return date.toLocaleDateString('es-ES', { dateStyle: 'medium' })
}

const formatDateInput = (date?: string): string => {
	if (!date) return ''
	return new Date(date).toISOString().slice(0, 10)
}

const DEFAULT_MINIMUM_GAP_DAYS = 3
const savedBankFiltersStorageKey = 'family-bank-saved-filters'
const defaultTransactionColumnWidths = [112, 190, 320, 190, 126, 126]
const minimumTransactionColumnWidths = [82, 96, 140, 126, 92, 92]
const defaultBankFilterState: BankFilterState = {
	search: '',
	dateFrom: '',
	dateUntil: '',
	amountMin: '',
	amountMax: '',
	transactionType: 'anything',
	sorting: 'dateDesc',
	category: '',
	subcategory: '',
}

const readSavedBankFilters = (): SavedBankFilter[] => {
	try {
		const rawValue = window.localStorage.getItem(savedBankFiltersStorageKey)
		if (!rawValue) return []
		const savedFilters = JSON.parse(rawValue)
		if (!Array.isArray(savedFilters)) return []
		return savedFilters
			.filter(savedFilter => savedFilter && typeof savedFilter.id === 'string' && typeof savedFilter.name === 'string' && savedFilter.filters)
			.map(savedFilter => ({
				id: savedFilter.id,
				name: savedFilter.name,
				filters: {
					...defaultBankFilterState,
					...savedFilter.filters,
					transactionType: savedFilter.filters.transactionType === 'debit' || savedFilter.filters.transactionType === 'credit' ? savedFilter.filters.transactionType : 'anything',
					sorting: savedFilter.filters.sorting === 'dateAsc' || savedFilter.filters.sorting === 'amount' ? savedFilter.filters.sorting : 'dateDesc',
				},
				createdAt: savedFilter.createdAt || new Date().toISOString(),
				updatedAt: savedFilter.updatedAt || savedFilter.createdAt || new Date().toISOString(),
			}))
	} catch {
		return []
	}
}

const writeSavedBankFilters = (savedFilters: SavedBankFilter[]) => {
	window.localStorage.setItem(savedBankFiltersStorageKey, JSON.stringify(savedFilters))
}

const bankFilterQueryKeys = ['q', 'from', 'until', 'min', 'max', 'type', 'sort', 'category', 'subcategory']

const parseBankFilterState = (): BankFilterState => {
	const params = new URLSearchParams(window.location.search)
	const transactionTypeParam = params.get('type')
	const sortingParam = params.get('sort')
	return {
		search: params.get('q') || '',
		dateFrom: params.get('from') || '',
		dateUntil: params.get('until') || '',
		amountMin: params.get('min') || '',
		amountMax: params.get('max') || '',
		transactionType: transactionTypeParam === 'debit' || transactionTypeParam === 'credit' ? transactionTypeParam : 'anything',
		sorting: sortingParam === 'dateAsc' || sortingParam === 'amount' ? sortingParam : 'dateDesc',
		category: params.get('category') || '',
		subcategory: params.get('subcategory') || '',
	}
}

const bankFilterUrl = (filters: BankFilterState): string => {
	const url = new URL(window.location.href)
	bankFilterQueryKeys.forEach(key => url.searchParams.delete(key))
	if (filters.search.trim()) url.searchParams.set('q', filters.search.trim())
	if (filters.dateFrom) url.searchParams.set('from', filters.dateFrom)
	if (filters.dateUntil) url.searchParams.set('until', filters.dateUntil)
	if (filters.amountMin) url.searchParams.set('min', filters.amountMin)
	if (filters.amountMax) url.searchParams.set('max', filters.amountMax)
	if (filters.transactionType !== 'anything') url.searchParams.set('type', filters.transactionType)
	if (filters.sorting !== 'dateDesc') url.searchParams.set('sort', filters.sorting)
	if (filters.category) url.searchParams.set('category', filters.category)
	if (filters.subcategory) url.searchParams.set('subcategory', filters.subcategory)
	return `${url.pathname}${url.search}${url.hash}`
}

const shouldShowDateRange = (range: EmptyDateRange, minimumGapDays: number): boolean => {
	return range.note === 'Inicio del historial' || range.days >= minimumGapDays
}

const isHistoryStart = (range: EmptyDateRange): boolean => {
	return range.note === 'Inicio del historial'
}

const formatMoney = (amount: number): string => {
	return amount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', useGrouping: true })
}

const formatSignedMoney = (amount: number): string => {
	const sign = amount > 0 ? '+' : ''
	return `${sign}${formatMoney(amount)}`
}

const normalizeTransactionText = (value: string): string => {
	return String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/\*+/g, ' ')
		.replace(/\d+/g, ' ')
		.replace(/[^a-zñ ]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
}

const formatSignedPercent = (ratio?: number): string => {
	if (ratio === undefined) return '-'
	const sign = ratio > 0 ? '+' : ''
	return `${sign}${ratio.toLocaleString('es-ES', { style: 'percent', maximumFractionDigits: 0 })}`
}

const formatPercent = (ratio: number): string => ratio.toLocaleString('es-ES', { style: 'percent', maximumFractionDigits: 0 })

const formatDayMonth = (date: Date): string => {
	return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', '').toLowerCase()
}

const formatDayMonthYear = (date: Date): string => {
	return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '').toLowerCase()
}

const formatCompactMoney = (amount: number): string => {
	const absAmount = Math.abs(amount)
	const sign = amount < 0 ? '-' : ''
	if (absAmount >= 1000) {
		return `${sign}${Math.round(absAmount / 1000).toLocaleString('es-ES')} K €`
	}
	return `${sign}${Math.round(absAmount).toLocaleString('es-ES')} €`
}

const formatRoundedMoney = (amount: number): string => {
	const roundedAmount = Math.round(amount)
	const sign = roundedAmount < 0 ? '-' : ''
	const groupedAmount = Math.abs(roundedAmount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
	return `${sign}${groupedAmount} €`
}

const formatInputDate = (value: string): string => {
	return formatDate(new Date(dateInputToTime(value)))
}

const getTransactionKey = (transaction: Transaction): string => {
	return transaction._id || transaction.hash || `${transaction.date}-${transaction.order}-${transaction.ref}-${transaction.amount}-${transaction.balance}`
}

const startOfInputDay = (value: string): string => {
	return `${value}T00:00:00.000Z`
}

const endOfInputDay = (value: string): string => {
	return `${value}T23:59:59.999Z`
}

const amountSectionTitles: Record<AmountSectionId, string> = {
	income: 'Ingresos',
	highestDebits: 'Gastos grandes',
	averageDebits: 'Gastos medios',
	lowestDebits: 'Gastos menores',
}

const transactionListParams = (
	filters: BankFilterState,
	cursor?: TransactionListCursor,
	amountSection?: AmountSectionId,
	amountThresholds?: AmountThresholds,
): URLSearchParams => {
	const params = new URLSearchParams()
	if (filters.search.trim()) params.append('search', filters.search.trim())
	if (cursor) {
		params.append('date', cursor.date)
		params.append('order', String(cursor.order))
		if (cursor.amount !== undefined) params.append('amount', String(cursor.amount))
	}
	if (filters.dateFrom) params.append('from', startOfInputDay(filters.dateFrom))
	if (filters.dateUntil) params.append('until', endOfInputDay(filters.dateUntil))
	if (filters.amountMin !== '' && filters.amountMax !== '') {
		params.append('min', filters.amountMin)
		params.append('max', filters.amountMax)
	}
	if (filters.transactionType === 'debit') {
		params.append('debits', 'true')
	} else if (filters.transactionType === 'credit') {
		params.append('credits', 'true')
	}
	if (filters.category) params.append('category', filters.category)
	if (filters.subcategory) params.append('subcategory', filters.subcategory)
	if (filters.sorting === 'amount') params.append('sort', 'amount')
	if (filters.sorting === 'dateAsc') params.append('direction', 'asc')
	if (amountSection) params.append('amountSection', amountSection)
	if (amountThresholds) {
		params.append('highestDebitsThreshold', String(amountThresholds.highestDebits))
		params.append('averageDebitsThreshold', String(amountThresholds.averageDebits))
	}
	return params
}

const responseError = async (response: Response, fallback: string): Promise<Error> => {
	let message = fallback
	try {
		const payload = await response.json()
		if (typeof payload?.message === 'string') message = payload.message
	} catch {
		// Keep the fallback when the API did not return JSON.
	}
	return new Error(`${message} (${response.status})`)
}

const dateInputToTime = (value: string): number => {
	return new Date(`${value}T00:00:00.000Z`).getTime()
}

const dateTimeToInput = (value: number): string => {
	return new Date(value).toISOString().slice(0, 10)
}

const clampNumber = (value: number, lower: number, upper: number): number => {
	return Math.min(Math.max(value, lower), upper)
}

const rangePercent = (value: number, lower: number, upper: number): number => {
	if (upper <= lower) return 0
	return ((value - lower) / (upper - lower)) * 100
}

const monthLabel = (key: string): string => {
	const [year, month] = key.split('-').map(Number)
	return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('es-ES', { month: 'short' }).replace('.', '')
}

const shortMonthLabel = (date: Date): string => {
	return date.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '').slice(0, 3)
}

const chartPeriodLabel = (period: ChartPeriod): string => {
	switch (period) {
		case 'year':
			return 'Último año'
		case 'three-years':
			return 'Últimos 3 años'
		default:
			return 'Último semestre'
	}
}

const categoryIds = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]

const categoryLabels: Record<number, string> = {
	0: 'Nómina',
	1: 'Subsidios',
	2: 'Inversiones',
	3: 'Impuestos',
	4: 'Comisiones',
	5: 'Inmobiliario',
	6: 'Tecnología',
	7: 'Hogar',
	8: 'Comunidad',
	9: 'Seguros',
	10: 'Suministros',
	11: 'Compra',
	12: 'Restaurantes',
	13: 'Transporte',
	14: 'Ocio',
	15: 'Salud',
	16: 'Cuidado personal',
	17: 'Educación',
	18: 'Ropa',
	19: 'Mascotas',
	20: 'Juego y azar',
}

const subcategoryLabels: Record<number, string> = {
	0: 'Empleador principal',
	1: 'Freelance',
	2: 'Desempleo',
	3: 'Familia',
	4: 'Pensión',
	5: 'Fondos',
	6: 'IRPF',
	7: 'IVA',
	8: 'Impuestos hogar',
	9: 'Impuestos coche',
	10: 'Cuentas',
	11: 'Tarjetas',
	12: 'Descubiertos',
	13: 'Hipoteca',
	14: 'Compraventa',
	15: 'Reformas',
	16: 'Alquiler',
	17: 'Muebles',
	18: 'Electrodomésticos',
	19: 'Decoración',
	20: 'Reparaciones',
	21: 'Dispositivos',
	22: 'Software',
	23: 'Hogar',
	24: 'Coche',
	25: 'Vida',
	26: 'Otros seguros',
	27: 'Luz',
	28: 'Agua',
	29: 'Gas',
	30: 'Telecomunicaciones',
	31: 'Restaurantes',
	32: 'Cafés',
	33: 'Dulces',
	34: 'Combustible',
	35: 'Transporte público',
	36: 'Parking',
	37: 'Mantenimiento coche',
	38: 'Alquiler coche',
	39: 'Juguetes',
	40: 'Espectáculos',
	41: 'Streaming',
	42: 'Actividades',
	43: 'Vacaciones',
	44: 'Médico',
	45: 'Farmacia',
	46: 'Ortopedia',
	47: 'Peluquería',
	48: 'Gimnasio',
	49: 'Cosmética',
	50: 'Colegios',
	51: 'Material escolar',
	52: 'Moda',
	53: 'Deporte',
	54: 'Veterinario',
	55: 'Comida mascotas',
	56: 'Cuidados',
	57: 'Lotería',
	58: 'Bingo',
	59: 'Apuestas',
	60: 'Peajes',
	61: 'Cuota',
	62: 'Derrama',
	63: 'Bebidas',
	64: 'Comida',
	65: 'Material',
}

const categorySubcategoryIds: Record<number, number[]> = {
	0: [0, 1],
	1: [2, 3],
	2: [4, 5],
	3: [6, 7, 8, 9],
	4: [10, 11, 12],
	5: [13, 14, 15, 16],
	6: [21, 22],
	7: [17, 18, 19, 20],
	8: [61, 62],
	9: [23, 24, 25, 26],
	10: [27, 28, 29, 30],
	11: [64, 65],
	12: [31, 32, 33, 63],
	13: [34, 35, 36, 37, 38, 60],
	14: [39, 40, 41, 42, 43],
	15: [44, 45, 46],
	16: [47, 48, 49],
	17: [50, 51],
	18: [52, 53],
	19: [54, 55, 56],
	20: [57, 58, 59],
}

const categoryLabel = (category?: number): string => category === undefined ? 'Sin categoría' : categoryLabels[category] || 'Sin categoría'

const subcategoryLabel = (subcategory?: number): string => subcategory === undefined ? 'Sin subcategoría' : subcategoryLabels[subcategory] || 'Sin subcategoría'

const labelSorter = new Intl.Collator('es', { sensitivity: 'base' })

const sortedCategoryIds = () => [...categoryIds].sort((first, second) => labelSorter.compare(categoryLabel(first), categoryLabel(second)))

const sortedSubcategoryIds = (subcategoryIds: number[]) => (
	[...subcategoryIds].sort((first, second) => labelSorter.compare(subcategoryLabel(first), subcategoryLabel(second)))
)

const transactionCategoryLabel = (transaction: Transaction): string => {
	if (transaction.category === undefined) return categoryLabel(undefined)
	if (transaction.subcategory === undefined) return categoryLabel(transaction.category)
	return `${categoryLabel(transaction.category)} · ${subcategoryLabel(transaction.subcategory)}`
}

const analyticsClassSymbols: Record<Exclude<TransactionAnalyticsClass, 'normal'>, string> = {
	extraordinary: '!',
	investment: '↗',
	transfer: '⇄',
	ignored: '×',
}

const monthStartInput = (key: string): string => {
	const [year, month] = key.split('-').map(Number)
	return dateTimeToInput(Date.UTC(year, month - 1, 1))
}

const monthEndInput = (key: string): string => {
	const [year, month] = key.split('-').map(Number)
	return dateTimeToInput(Date.UTC(year, month, 0))
}

const shiftedIncomeMonthRange = (key: string): { from: string, until: string } => {
	const [year, month] = key.split('-').map(Number)
	return {
		from: dateTimeToInput(Date.UTC(year, month - 2, 16)),
		until: dateTimeToInput(Date.UTC(year, month - 1, 15)),
	}
}

const authResultMessage = (authResult?: string): string | undefined => {
	switch (authResult) {
		case 'missing-code':
			return 'No se pudo completar la verificación. Asegúrate de no estar conectado a una red WiFi y de que tu teléfono tiene conexión de datos móviles. Escribe tu nombre para intentarlo de nuevo.'
		case 'expired':
			return 'La verificación ha caducado. Escribe tu nombre para intentarlo de nuevo.'
		case 'denied':
			return 'Este teléfono no está autorizado para acceder a banca.'
		case 'failed':
			return 'La verificación ha fallado. Escribe tu nombre para intentarlo de nuevo.'
		case 'missing-name':
			return 'Escribe tu nombre para verificar tu teléfono.'
		case 'unknown-name':
			return 'Ese nombre no está autorizado.'
		default:
			return undefined
	}
}

const Banca = () => {
	const initialFilterState = parseBankFilterState()
	const [transactions, setTransactions] = useState<Transaction[]>([])
	const [emptyDateRanges, setEmptyDateRanges] = useState<EmptyDateRange[]>([])
	const [minimumGapDays, setMinimumGapDays] = useState(DEFAULT_MINIMUM_GAP_DAYS)
	const [showDateGaps, setShowDateGaps] = useState(false)
	const [hasDateGapsModalImport, setHasDateGapsModalImport] = useState(false)
	const [dataRefreshVersion, setDataRefreshVersion] = useState(0)
	const [bounds, setBounds] = useState<TransactionListBounds | undefined>()
	const [dashboardSummary, setDashboardSummary] = useState<BankDashboardSummary>({})
	const [chartData, setChartData] = useState<BankCharts>({ balance: [], monthlyFlow: [], categoryBreakdown: [] })
	const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('semester')
	const [isChartLoading, setIsChartLoading] = useState(false)
	const [search, setSearch] = useState(initialFilterState.search)
	const [dateFrom, setDateFrom] = useState(initialFilterState.dateFrom)
	const [dateUntil, setDateUntil] = useState(initialFilterState.dateUntil)
	const [amountMin, setAmountMin] = useState(initialFilterState.amountMin)
	const [amountMax, setAmountMax] = useState(initialFilterState.amountMax)
	const [draftSearch, setDraftSearch] = useState(initialFilterState.search)
	const [draftDateFrom, setDraftDateFrom] = useState(initialFilterState.dateFrom)
	const [draftDateUntil, setDraftDateUntil] = useState(initialFilterState.dateUntil)
	const [draftAmountMin, setDraftAmountMin] = useState(initialFilterState.amountMin)
	const [draftAmountMax, setDraftAmountMax] = useState(initialFilterState.amountMax)
	const [transactionType, setTransactionType] = useState<TransactionTypeFilter>(initialFilterState.transactionType)
	const [sorting, setSorting] = useState<TransactionSorting>(initialFilterState.sorting)
	const [category, setCategory] = useState(initialFilterState.category)
	const [subcategory, setSubcategory] = useState(initialFilterState.subcategory)
	const [categoryDrilldown, setCategoryDrilldown] = useState<string | undefined>(initialFilterState.category || undefined)
	const [draftTransactionType, setDraftTransactionType] = useState<TransactionTypeFilter>(initialFilterState.transactionType)
	const [draftSorting, setDraftSorting] = useState<TransactionSorting>(initialFilterState.sorting)
	const [draftCategory, setDraftCategory] = useState(initialFilterState.category)
	const [savedFilters, setSavedFilters] = useState<SavedBankFilter[]>(readSavedBankFilters)
	const [savedFilterName, setSavedFilterName] = useState('')
	const [selectedSavedFilterId, setSelectedSavedFilterId] = useState('')
	const [nextListCursor, setNextListCursor] = useState<any | undefined>()
	const [listCursor, setListCursor] = useState<any | undefined>()
	const [isLoading, setIsLoading] = useState(false)
	const [listError, setListError] = useState('')
	const [includeNonNormalAnalytics, setIncludeNonNormalAnalytics] = useState(false)
	const [amountSectionPages, setAmountSectionPages] = useState<AmountSection[]>([])
	const [amountThresholds, setAmountThresholds] = useState<AmountThresholds | undefined>()
	const [loadingAmountSection, setLoadingAmountSection] = useState<AmountSectionId | undefined>()
	const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false)
	const [balanceTooltip, setBalanceTooltip] = useState<BalanceTooltip | undefined>()
	const [averageBalanceTooltip, setAverageBalanceTooltip] = useState<AverageBalanceTooltip | undefined>()
	const [flowTooltip, setFlowTooltip] = useState<FlowTooltip | undefined>()
	const [categoryTooltip, setCategoryTooltip] = useState<CategoryTooltip | undefined>()
	const [editingCategoryKey, setEditingCategoryKey] = useState<string | undefined>()
	const [savingCategoryKey, setSavingCategoryKey] = useState<string | undefined>()
	const [hasManualCategoryChanges, setHasManualCategoryChanges] = useState(false)
	const [isCategorizingTransactions, setIsCategorizingTransactions] = useState(false)
	const [collapsedAmountSections, setCollapsedAmountSections] = useState<AmountSectionId[]>([])
	const [authName, setAuthName] = useState(bankAuthorizationName())
	const [authNameInput, setAuthNameInput] = useState(bankAuthorizationName())
	const [authNameSubmitted, setAuthNameSubmitted] = useState(false)
	const [consumedAuthSuccess] = useState(consumeSuccessfulBankAuthorization)
	const [mobileQrCodeUrl, setMobileQrCodeUrl] = useState('')
	const [mobileAuthorizationExpired, setMobileAuthorizationExpired] = useState(false)
	const [transactionColumnWidths, setTransactionColumnWidths] = useState(defaultTransactionColumnWidths)
	const [conceptModalConcept, setConceptModalConcept] = useState('')
	const [conceptModalField, setConceptModalField] = useState<BulkCategorizationField>('concept')
	const [conceptSummary, setConceptSummary] = useState<BankConceptSummary | undefined>()
	const [isConceptSummaryLoading, setIsConceptSummaryLoading] = useState(false)
	const [conceptCategory, setConceptCategory] = useState('')
	const [conceptSubcategory, setConceptSubcategory] = useState('')
	const [isConceptCategorizing, setIsConceptCategorizing] = useState(false)

	const observerTarget = useRef<HTMLDivElement>(null)
	const currentAuthResult = bankAuthorizationResult()
	const authMessage = authResultMessage(currentAuthResult)
	const shouldShowMobileRetry = !!currentAuthResult && authNameInput.trim() !== ''
	const hasAuthName = authName.trim() !== ''
	const shouldShowNameForm = !!currentAuthResult || (!hasBankAuthorizationSession() && !consumedAuthSuccess && (!hasAuthName || !authNameSubmitted))
	const shouldShowAuthorizationProgress = !shouldShowNameForm && !hasBankAuthorizationSession() && !consumedAuthSuccess
	const querySignature = JSON.stringify({
		search,
		dateFrom,
		dateUntil,
		amountMin,
		amountMax,
		transactionType,
		sorting,
		category,
		subcategory,
	})
	const currentAppliedFilters: BankFilterState = {
		search,
		dateFrom,
		dateUntil,
		amountMin,
		amountMax,
		transactionType,
		sorting,
		category,
		subcategory,
	}
	const transactionGridStyle = {
		'--transaction-date-width': `${transactionColumnWidths[0]}px`,
		'--transaction-concept-width': `${transactionColumnWidths[1]}px`,
		'--transaction-description-width': `${transactionColumnWidths[2]}px`,
		'--transaction-category-width': `${transactionColumnWidths[3]}px`,
		'--transaction-amount-width': `${transactionColumnWidths[4]}px`,
		'--transaction-balance-width': `${transactionColumnWidths[5]}px`,
		minWidth: `${transactionColumnWidths.reduce((total, width) => total + width, 0)}px`,
	} as CSSProperties
	const previousQuerySignature = useRef<string | undefined>()
	const visibleDateRanges = emptyDateRanges.filter(range => shouldShowDateRange(range, minimumGapDays))
	const visibleMovementGaps = visibleDateRanges.filter(range => !isHistoryStart(range))
	const longestGap = visibleMovementGaps.reduce<EmptyDateRange | undefined>((longest, range) => {
		if (!longest || range.days > longest.days) return range
		return longest
	}, undefined)
	const latestSummary = dashboardSummary.latest
	const forecast = dashboardSummary.savingForecast
	const monthScore = dashboardSummary.monthScore
	const yearScore = dashboardSummary.yearScore
	const monthReferenceDateLabel = monthScore?.referenceDate
		? formatDayMonthYear(new Date(monthScore.referenceDate))
		: ''
	const historyReferenceDateLabel = yearScore?.referenceDate
		? formatDayMonth(new Date(yearScore.referenceDate))
		: ''
	const metricClassName = (value?: number): string | undefined => value === undefined ? undefined : value >= 0 ? 'positive' : 'negative'
	const transactionsByKey = new Map(transactions.map(transaction => [getTransactionKey(transaction), transaction]))
	const amountSections = amountSectionPages.map(section => ({
		...section,
		transactions: section.transactionKeys
			.map(key => transactionsByKey.get(key))
			.filter((transaction): transaction is Transaction => transaction !== undefined),
	}))
	const amountCredits = amountSections.find(section => section.id === 'income')?.totalAmount || 0
	const amountDebitsAbs = Math.abs(amountSections
		.filter(section => section.id !== 'income')
		.reduce((total, section) => total + section.totalAmount, 0))
	const chartBalancePoints = chartData.balance
	const chartAverageBalancePoints = chartData.balanceAverage || []
	const dailyBalancePoints = chartBalancePoints.reduce<DailyBalancePoint[]>((days, point) => {
		const date = new Date(point.date).toISOString().slice(0, 10)
		const existing = days.find(day => day.date === date)
		if (existing) {
			existing.balance = point.balance
		} else {
			days.push({ date, balance: point.balance })
		}
		return days
	}, [])
	const dailyAverageBalancePoints = chartAverageBalancePoints.map(point => ({
		date: new Date(point.date).toISOString().slice(0, 10),
		balance: point.balance,
		samples: point.samples || [],
	}))
	const balanceValues = [...dailyBalancePoints, ...dailyAverageBalancePoints].map(point => point.balance)
	const balanceMin = balanceValues.length > 0 ? Math.min(...balanceValues) : 0
	const balanceMax = balanceValues.length > 0 ? Math.max(...balanceValues) : 0
	const balanceRange = Math.max(balanceMax - balanceMin, 1)
	const balanceChartWidth = 1900
	const balanceChartHeight = 250
	const balanceChartPadding = { top: 18, right: 18, bottom: 34, left: 58 }
	const balancePlotWidth = balanceChartWidth - balanceChartPadding.left - balanceChartPadding.right
	const balancePlotHeight = balanceChartHeight - balanceChartPadding.top - balanceChartPadding.bottom
	const balanceSeriesDates = [...dailyBalancePoints, ...dailyAverageBalancePoints].map(point => dateInputToTime(point.date))
	const balanceSeriesStart = balanceSeriesDates.length > 0 ? Math.min(...balanceSeriesDates) : 0
	const balanceSeriesEnd = balanceSeriesDates.length > 0 ? Math.max(...balanceSeriesDates) : balanceSeriesStart
	const balanceSeriesDuration = Math.max(balanceSeriesEnd - balanceSeriesStart, 1)
	const balanceX = (date: string) => balanceChartPadding.left + ((dateInputToTime(date) - balanceSeriesStart) / balanceSeriesDuration) * balancePlotWidth
	const balanceMonthTicks = (() => {
		if (balanceSeriesDates.length === 0) return []
		const ticks: { date: string, label: string, x: number }[] = []
		const startDate = new Date(balanceSeriesStart)
		const firstMonth = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + 1, 1))
		for (let tick = firstMonth; tick.getTime() < balanceSeriesEnd; tick = new Date(Date.UTC(tick.getUTCFullYear(), tick.getUTCMonth() + 1, 1))) {
			const inputDate = tick.toISOString().slice(0, 10)
			ticks.push({
				date: inputDate,
				label: shortMonthLabel(tick),
				x: balanceX(inputDate),
			})
		}
		return ticks
	})()
	const balancePoints = dailyBalancePoints.map((point, index) => {
		const x = balanceX(point.date)
		const y = balanceChartPadding.top + ((balanceMax - point.balance) / balanceRange) * balancePlotHeight
		return { x, y, point }
	})
	const averageBalancePoints = dailyAverageBalancePoints.map(point => {
		const x = balanceX(point.date)
		const y = balanceChartPadding.top + ((balanceMax - point.balance) / balanceRange) * balancePlotHeight
		return { x, y, point }
	})
	const balancePath = balancePoints.map(point => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')
	const averageBalancePath = averageBalancePoints.map(point => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')
	const latestBalancePoint = balancePoints[balancePoints.length - 1]
	const hoveredBalancePoint = balanceTooltip ? balancePoints[balanceTooltip.index] : undefined
	const hoveredPreviousBalancePoint = balanceTooltip ? balancePoints[balanceTooltip.index - 1] : undefined
	const hoveredAverageBalancePoint = averageBalanceTooltip ? averageBalancePoints[averageBalanceTooltip.index] : undefined
	const balanceTooltipWidth = 207
	const balanceTooltipHeight = 81
	const balanceTooltipX = hoveredBalancePoint
		? clampNumber((balanceTooltip?.x ?? hoveredBalancePoint.x) + 12, 8, balanceChartWidth - balanceTooltipWidth - 8)
		: 0
	const balanceTooltipY = hoveredBalancePoint
		? clampNumber((balanceTooltip?.y ?? hoveredBalancePoint.y) + 12, 8, balanceChartHeight - balanceTooltipHeight - 8)
		: 0
	const averageBalanceTooltipSamples = hoveredAverageBalancePoint?.point.samples || []
	const averageBalanceTooltipWidth = 266
	const averageBalanceTooltipHeight = Math.max(40, 14 + averageBalanceTooltipSamples.length * 22)
	const averageBalanceTooltipX = hoveredAverageBalancePoint
		? clampNumber((averageBalanceTooltip?.x ?? hoveredAverageBalancePoint.x) + 12, 8, balanceChartWidth - averageBalanceTooltipWidth - 8)
		: 0
	const averageBalanceTooltipY = hoveredAverageBalancePoint
		? clampNumber((averageBalanceTooltip?.y ?? hoveredAverageBalancePoint.y) + 12, 8, balanceChartHeight - averageBalanceTooltipHeight - 8)
		: 0
	const monthlyFlow = chartData.monthlyFlow.map(month => ({
		...month,
		label: monthLabel(month.month),
	} as MonthlyFlow))
	const secondaryChartWidth = 980
	const secondaryChartHeight = 420
	const secondaryChartVerticalPadding = secondaryChartHeight * 0.05
	const flowChartWidth = secondaryChartWidth
	const flowChartHeight = secondaryChartHeight
	const flowChartVerticalPadding = secondaryChartVerticalPadding + (secondaryChartHeight - secondaryChartVerticalPadding * 2) * 0.075
	const flowChartPadding = { top: flowChartVerticalPadding, right: 36, bottom: flowChartVerticalPadding, left: 34 }
	const flowPlotWidth = flowChartWidth - flowChartPadding.left - flowChartPadding.right
	const flowPlotHeight = flowChartHeight - flowChartPadding.top - flowChartPadding.bottom
	const maxFlowValue = Math.max(...monthlyFlow.flatMap(month => [month.income, month.expenses]), 1)
	const averageIncome = monthlyFlow.length > 0 ? monthlyFlow.reduce((total, month) => total + month.income, 0) / monthlyFlow.length : 0
	const averageExpenses = monthlyFlow.length > 0 ? monthlyFlow.reduce((total, month) => total + month.expenses, 0) / monthlyFlow.length : 0
	const averageIncomeY = flowChartPadding.top + flowPlotHeight - (averageIncome / maxFlowValue) * flowPlotHeight
	const averageExpensesY = flowChartPadding.top + flowPlotHeight - (averageExpenses / maxFlowValue) * flowPlotHeight
	const flowTooltipWidth = 168
	const flowTooltipHeight = 67
	const flowTooltipX = flowTooltip ? clampNumber(flowTooltip.x + 10, 8, flowChartWidth - flowTooltipWidth - 8) : 0
	const flowTooltipY = flowTooltip ? clampNumber(flowTooltip.y + 10, 8, flowChartHeight - flowTooltipHeight - 8) : 0
	const categoryColors = ['#179ead', '#00875c', '#be4545', '#d99a2b', '#6f7bdc', '#b15bc8', '#2c7a9f', '#8a6f36']
	const categoryBreakdown = chartData.categoryBreakdown || []
	const categoryAmount = (item: BankChartCategoryBreakdown): number => item.income - (item.projectedExpenses ?? item.expenses)
	const categoryHistoricalShare = (item: BankChartCategoryBreakdown): number => item.historicalNetShare || 0
	const subcategoryHistoricalShare = (item: BankChartCategoryBreakdown): number => item.historicalSubcategoryNetShare || 0
	const categoryHistoricalAmount = (item: BankChartCategoryBreakdown): number => item.historicalNetAmount || 0
	const subcategoryHistoricalAmount = (item: BankChartCategoryBreakdown): number => item.historicalSubcategoryNetAmount || 0
	const categoryChartItems = categoryDrilldown
		? categoryBreakdown
			.filter(item => String(item.category ?? 'uncategorized') === categoryDrilldown)
			.map(item => ({
				id: String(item.subcategory ?? 'uncategorized'),
				category: item.category,
				subcategory: item.subcategory,
				label: subcategoryLabel(item.subcategory),
				amount: categoryAmount(item),
				historicalShare: subcategoryHistoricalShare(item),
				historicalAmount: subcategoryHistoricalAmount(item),
				count: item.count,
			}))
		: Array.from(categoryBreakdown.reduce<Map<string, { category?: number, amount: number, historicalShare: number, historicalAmount: number, count: number }>>((items, item) => {
			const key = String(item.category ?? 'uncategorized')
			const existing = items.get(key) || { category: item.category, amount: 0, historicalShare: 0, historicalAmount: 0, count: 0 }
			existing.amount += categoryAmount(item)
			existing.historicalShare = Math.max(existing.historicalShare, categoryHistoricalShare(item))
			existing.historicalAmount = Math.max(existing.historicalAmount, categoryHistoricalAmount(item))
			existing.count += item.count
			items.set(key, existing)
			return items
		}, new Map()).values()).map(item => ({
			id: String(item.category ?? 'uncategorized'),
			category: item.category,
			subcategory: undefined,
			label: categoryLabel(item.category),
			amount: item.amount,
			historicalShare: item.historicalShare,
			historicalAmount: item.historicalAmount,
			count: item.count,
		}))
	if (!categoryDrilldown && !categoryChartItems.some(item => item.category === undefined)) {
		categoryChartItems.push({
			id: 'uncategorized',
			category: undefined,
			subcategory: undefined,
			label: categoryLabel(undefined),
			amount: 0,
			historicalShare: 0,
			historicalAmount: 0,
			count: 0,
		})
	}
	if (categoryDrilldown === 'uncategorized' && !categoryChartItems.some(item => item.subcategory === undefined)) {
		categoryChartItems.push({
			id: 'uncategorized',
			category: undefined,
			subcategory: undefined,
			label: subcategoryLabel(undefined),
			amount: 0,
			historicalShare: 0,
			historicalAmount: 0,
			count: 0,
		})
	}
	const categorySliceItems = categoryChartItems
		.filter(item => item.amount < 0)
		.sort((first, second) => (second.historicalShare || 0) - (first.historicalShare || 0) || Math.abs(second.amount) - Math.abs(first.amount))
	const categoryChartTotal = categorySliceItems.reduce((total, item) => total + Math.abs(item.amount), 0)
	const categoryBarItems = categorySliceItems.slice(0, 10)
	const categoryRestItems = categorySliceItems.slice(10)
	const categoryRestSummary = categoryRestItems
		.map(item => `${item.label} ${formatPercent(categoryChartTotal > 0 ? Math.abs(item.amount) / categoryChartTotal : 0)}`)
		.join('; ')
	const categoryBarColor = (item: { category?: number, subcategory?: number }, index: number): string => {
		const colorKey = categoryDrilldown ? item.subcategory : item.category
		return categoryColors[Math.abs(colorKey ?? index) % categoryColors.length]
	}
	const categoryPeriodMonths = chartData.range
		? Math.max(1, (new Date(chartData.range.upper).getTime() - new Date(chartData.range.lower).getTime()) / (30.4375 * 24 * 60 * 60 * 1000))
		: 1
	const categoryBarMaxShare = Math.max(
		...categoryBarItems.flatMap(item => [
			categoryChartTotal > 0 ? Math.abs(item.amount) / categoryChartTotal : 0,
			categoryChartTotal > 0 ? (item.historicalAmount || 0) / categoryChartTotal : 0,
		]),
		0.01,
	)
	const categoryChartWidth = secondaryChartWidth
	const categoryChartHeight = secondaryChartHeight
	const categoryChartPadding = { top: secondaryChartVerticalPadding, right: 230, bottom: secondaryChartVerticalPadding, left: 190 }
	const categoryBarRowHeight = 22
	const categoryRestHeight = categoryRestItems.length > 0 ? 28 : 0
	const categoryBarsHeight = categoryChartHeight - categoryChartPadding.top - categoryChartPadding.bottom - categoryRestHeight
	const categoryBarGap = categoryBarItems.length > 1
		? Math.max(8, (categoryBarsHeight - categoryBarItems.length * categoryBarRowHeight) / (categoryBarItems.length - 1))
		: 0
	const categoryRestY = categoryChartHeight - categoryChartPadding.bottom + 1
	const categoryBarPlotWidth = categoryChartWidth - categoryChartPadding.left - categoryChartPadding.right
	const categoryTooltipWidth = 260
	const categoryTooltipHeight = 67
	const categoryTooltipX = categoryTooltip ? clampNumber(categoryTooltip.x + 10, 8, categoryChartWidth - categoryTooltipWidth - 8) : 0
	const categoryTooltipY = categoryTooltip ? clampNumber(categoryTooltip.y + 10, 8, categoryChartHeight - categoryTooltipHeight - 8) : 0
	const chartRangeLabel = chartData.range ? `${chartPeriodLabel(chartPeriod)}: ${formatDate(new Date(chartData.range.lower))} - ${formatDate(new Date(chartData.range.upper))}` : chartPeriodLabel(chartPeriod)
	const categoryChartTitle = categoryDrilldown ? categoryLabel(categoryDrilldown === 'uncategorized' ? undefined : Number(categoryDrilldown)) : 'Categorías'
	const renderChartPeriodControl = () => (
		<label className="chart-period-control">
			<span>Periodo</span>
			<select value={chartPeriod} onChange={event => setChartPeriod(event.target.value as ChartPeriod)}>
				<option value="semester">Semestre</option>
				<option value="year">Año</option>
				<option value="three-years">3 años</option>
			</select>
		</label>
	)
	const renderAnalyticsClassToggle = () => (
		<label className="chart-analytics-toggle" title="Incluye movimientos extraordinarios, inversiones, transferencias e ignorados">
			<input
				type="checkbox"
				checked={includeNonNormalAnalytics}
				onChange={event => setIncludeNonNormalAnalytics(event.target.checked)}
			/>
			<span>Incluir movimientos no ordinarios</span>
		</label>
	)
	const isFiltering = search.trim() !== '' || dateFrom !== '' || dateUntil !== '' || amountMin !== '' || amountMax !== '' || transactionType !== 'anything' || sorting !== 'dateDesc' || category !== '' || subcategory !== ''
	const dateLowerInput = formatDateInput(bounds?.dates?.lower)
	const dateUpperInput = formatDateInput(bounds?.dates?.upper)
	const hasDateBounds = dateLowerInput !== '' && dateUpperInput !== ''
	const dateLowerValue = hasDateBounds ? dateInputToTime(dateLowerInput) : 0
	const dateUpperValue = hasDateBounds ? Math.max(dateLowerValue, dateInputToTime(dateUpperInput)) : 1
	const selectedDateFromValue = hasDateBounds && draftDateFrom ? clampNumber(dateInputToTime(draftDateFrom), dateLowerValue, dateUpperValue) : dateLowerValue
	const selectedDateUntilValue = hasDateBounds && draftDateUntil ? clampNumber(dateInputToTime(draftDateUntil), dateLowerValue, dateUpperValue) : dateUpperValue
	const dateRangeStartPercent = rangePercent(selectedDateFromValue, dateLowerValue, dateUpperValue)
	const dateRangeEndPercent = rangePercent(selectedDateUntilValue, dateLowerValue, dateUpperValue)
	const amountLowerValue = Math.floor(bounds?.amounts?.lower ?? 0)
	const amountUpperValue = Math.max(amountLowerValue + 1, Math.ceil(bounds?.amounts?.upper ?? 1))
	const selectedAmountMinValue = clampNumber(draftAmountMin !== '' ? Number(draftAmountMin) : amountLowerValue, amountLowerValue, amountUpperValue)
	const selectedAmountMaxValue = clampNumber(draftAmountMax !== '' ? Number(draftAmountMax) : amountUpperValue, amountLowerValue, amountUpperValue)
	const amountRangeStartPercent = rangePercent(selectedAmountMinValue, amountLowerValue, amountUpperValue)
	const amountRangeEndPercent = rangePercent(selectedAmountMaxValue, amountLowerValue, amountUpperValue)
	const filtersChanged = draftSearch !== search || draftDateFrom !== dateFrom || draftDateUntil !== dateUntil || draftAmountMin !== amountMin || draftAmountMax !== amountMax || draftTransactionType !== transactionType || draftSorting !== sorting || draftCategory !== category
	const transactionTypeSummary = transactionType === 'debit' ? 'Gastos' : transactionType === 'credit' ? 'Ingresos' : 'Ambos'
	const sortingSummary = sorting === 'dateAsc' ? 'Antiguos' : sorting === 'amount' ? 'Importe' : 'Recientes'
	const dateSummary = dateFrom || dateUntil
		? `Fechas: ${dateFrom ? formatInputDate(dateFrom) : 'inicio'} - ${dateUntil ? formatInputDate(dateUntil) : 'hoy'}`
		: 'Todas las fechas'
	const amountSummary = amountMin !== '' || amountMax !== ''
		? `Importe: ${amountMin !== '' ? formatMoney(Number(amountMin)) : 'mín.'} - ${amountMax !== '' ? formatMoney(Number(amountMax)) : 'máx.'}`
		: 'Todos los importes'
	const categorySummary = category !== ''
		? `Categoría: ${subcategory !== '' ? subcategoryLabel(subcategory === 'uncategorized' ? undefined : Number(subcategory)) : categoryLabel(category === 'uncategorized' ? undefined : Number(category))}`
		: 'Todas las categorías'
	const appliedFilterSummary = [
		search.trim() ? `Buscar: ${search.trim()}` : 'Sin búsqueda',
		`Tipo: ${transactionTypeSummary}`,
		`Orden: ${sortingSummary}`,
		dateSummary,
		amountSummary,
		categorySummary,
	].join(' · ')

	const updateDateRange = (nextFrom: number, nextUntil: number) => {
		if (!hasDateBounds) return
		const clampedFrom = clampNumber(Math.min(nextFrom, nextUntil), dateLowerValue, dateUpperValue)
		const clampedUntil = clampNumber(Math.max(nextFrom, nextUntil), dateLowerValue, dateUpperValue)
		setDraftDateFrom(clampedFrom <= dateLowerValue ? '' : dateTimeToInput(clampedFrom))
		setDraftDateUntil(clampedUntil >= dateUpperValue ? '' : dateTimeToInput(clampedUntil))
	}

	const updateAmountRange = (nextMin: number, nextMax: number) => {
		const clampedMin = clampNumber(Math.min(nextMin, nextMax), amountLowerValue, amountUpperValue)
		const clampedMax = clampNumber(Math.max(nextMin, nextMax), amountLowerValue, amountUpperValue)
		if (clampedMin <= amountLowerValue && clampedMax >= amountUpperValue) {
			setDraftAmountMin('')
			setDraftAmountMax('')
			return
		}
		setDraftAmountMin(String(clampedMin))
		setDraftAmountMax(String(clampedMax))
	}

	const updateBalanceTooltip = (index: number, event: ReactMouseEvent<SVGElement>) => {
		const svg = event.currentTarget.ownerSVGElement
		if (!svg) return
		const rect = svg.getBoundingClientRect()
		setAverageBalanceTooltip(undefined)
		setBalanceTooltip({
			index,
			x: ((event.clientX - rect.left) / rect.width) * balanceChartWidth,
			y: ((event.clientY - rect.top) / rect.height) * balanceChartHeight,
		})
	}

	const updateAverageBalanceTooltip = (index: number, event: ReactMouseEvent<SVGElement>) => {
		const svg = event.currentTarget.ownerSVGElement
		if (!svg) return
		const rect = svg.getBoundingClientRect()
		setBalanceTooltip(undefined)
		setAverageBalanceTooltip({
			index,
			x: ((event.clientX - rect.left) / rect.width) * balanceChartWidth,
			y: ((event.clientY - rect.top) / rect.height) * balanceChartHeight,
		})
	}

	const updateFlowTooltip = (month: MonthlyFlow, kind: 'income' | 'expenses', amount: number, event: ReactMouseEvent<SVGElement>) => {
		const svg = event.currentTarget.ownerSVGElement
		if (!svg) return
		const rect = svg.getBoundingClientRect()
		setFlowTooltip({
			month: month.month,
			label: month.label,
			kind,
			amount,
			x: ((event.clientX - rect.left) / rect.width) * flowChartWidth,
			y: ((event.clientY - rect.top) / rect.height) * flowChartHeight,
		})
	}

	const updateCategoryTooltip = (item: { label: string, amount: number, currentShare: number, monthlyAmount?: number, historicalShare?: number, historicalAmount?: number }, event: ReactMouseEvent<SVGElement>) => {
		const svg = event.currentTarget.ownerSVGElement
		if (!svg) return
		const rect = svg.getBoundingClientRect()
		setCategoryTooltip({
			label: item.label,
			amount: item.amount,
			currentShare: item.currentShare,
			monthlyAmount: item.monthlyAmount,
			historicalShare: item.historicalShare,
			historicalAmount: item.historicalAmount,
			x: ((event.clientX - rect.left) / rect.width) * categoryChartWidth,
			y: ((event.clientY - rect.top) / rect.height) * categoryChartHeight,
		})
	}

	const updateTransactionCategory = (transaction: Transaction, nextCategory?: number, nextSubcategory?: number) => {
		if (!transaction._id) return
		const key = getTransactionKey(transaction)
		setSavingCategoryKey(key)
		apiFetch(`/bank/${transaction._id}`, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				category: nextCategory ?? null,
				subcategory: nextSubcategory ?? null,
			}),
		})
			.then(response => {
				if (!response.ok) {
					throw new Error(`Category update failed with status ${response.status}`)
				}
				setTransactions(prev => prev.map(item => (
					getTransactionKey(item) === key
						? {
							...item,
							category: nextCategory,
							subcategory: nextSubcategory,
						}
						: item
				)))
				setHasManualCategoryChanges(true)
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error('Error updating transaction category:', error)
			})
			.finally(() => {
				setSavingCategoryKey(undefined)
			})
	}

	const updateTransactionAnalyticsClass = (transaction: Transaction, analyticsClass: TransactionAnalyticsClass) => {
		if (!transaction._id) return
		const key = getTransactionKey(transaction)
		setSavingCategoryKey(key)
		apiFetch(`/bank/${transaction._id}`, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ analyticsClass }),
		})
			.then(response => {
				if (!response.ok) {
					throw new Error(`Analytics class update failed with status ${response.status}`)
				}
				setTransactions(prev => prev.map(item => (
					getTransactionKey(item) === key
						? {
							...item,
							analyticsClass,
							analyticsClassSource: 'manual',
							suggestedAnalyticsClass: undefined,
							suggestionReason: undefined,
							suggestionConfidence: undefined,
						}
						: item
				)))
				setDataRefreshVersion(version => version + 1)
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error('Error updating transaction analytics class:', error)
			})
			.finally(() => {
				setSavingCategoryKey(undefined)
			})
	}

	const openBulkCategorizationModal = (field: BulkCategorizationField, value: string) => {
		setConceptModalField(field)
		setConceptModalConcept(value)
		setConceptSummary(undefined)
		setConceptCategory('')
		setConceptSubcategory('')
		setIsConceptSummaryLoading(true)
		const params = new URLSearchParams({ [field]: value })
		const summaryPath = field === 'concept' ? '/bank/concepts/summary' : '/bank/descriptions/summary'
		apiFetch(`${summaryPath}?${params.toString()}`)
			.then(response => {
				if (!response.ok) {
					throw new Error(`${field} summary failed with status ${response.status}`)
				}
				return response.json()
			})
			.then((summary: BankConceptSummary) => {
				setConceptSummary(summary)
				const preferredCategory = summary.categories.find(item => item.category !== undefined)
				if (preferredCategory?.category !== undefined) {
					setConceptCategory(String(preferredCategory.category))
					setConceptSubcategory(preferredCategory.subcategory === undefined ? '' : String(preferredCategory.subcategory))
				}
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error(`Error loading ${field} summary:`, error)
			})
			.finally(() => {
				setIsConceptSummaryLoading(false)
			})
	}

	const openConceptModal = (concept: string) => openBulkCategorizationModal('concept', concept)

	const openDescriptionModal = (description: string) => openBulkCategorizationModal('description', description)

	const closeConceptModal = () => {
		if (isConceptCategorizing) return
		setConceptModalConcept('')
		setConceptSummary(undefined)
	}

	const categorizeConcept = () => {
		if (!conceptSummary || isConceptCategorizing) return
		const nextCategory = conceptCategory === '' ? undefined : Number(conceptCategory)
		const nextSubcategory = conceptSubcategory === '' ? undefined : Number(conceptSubcategory)
		const nextLabel = nextCategory === undefined
			? 'Sin categoría'
			: nextSubcategory === undefined
				? categoryLabel(nextCategory)
				: `${categoryLabel(nextCategory)} · ${subcategoryLabel(nextSubcategory)}`
		const confirmed = window.confirm(`Categorizar ${conceptSummary.count.toLocaleString('es-ES')} movimientos como ${nextLabel}?`)
		if (!confirmed) return
		setIsConceptCategorizing(true)
		const categorizationPath = conceptModalField === 'concept' ? '/bank/concepts' : '/bank/descriptions'
		apiFetch(categorizationPath, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				[conceptModalField]: conceptModalConcept,
				category: nextCategory ?? null,
				subcategory: nextSubcategory ?? null,
			}),
		})
			.then(response => {
				if (!response.ok) {
					throw new Error(`Concept categorization failed with status ${response.status}`)
				}
				return response.json()
			})
			.then(() => {
				const normalizedValue = normalizeTransactionText(conceptModalConcept)
				setTransactions(prev => prev.map(transaction => (
					normalizeTransactionText(transaction[conceptModalField]) === normalizedValue
						? { ...transaction, category: nextCategory, subcategory: nextSubcategory }
						: transaction
				)))
				setHasManualCategoryChanges(true)
				closeConceptModal()
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error(`Error categorizing ${conceptModalField}:`, error)
			})
			.finally(() => {
				setIsConceptCategorizing(false)
			})
	}

	const renderConceptCell = (transaction: Transaction) => (
		<button type="button" className="concept-cell-button" onClick={() => openConceptModal(transaction.concept)} title="Ver resumen del concepto">
			{transaction.concept}
		</button>
	)

	const renderDescriptionCell = (transaction: Transaction) => (
		<button type="button" className="concept-cell-button" onClick={() => openDescriptionModal(transaction.description)} title="Ver resumen de la descripción">
			{transaction.description}
		</button>
	)

	const renderCategoryCell = (transaction: Transaction) => {
		const key = getTransactionKey(transaction)
		const isEditing = editingCategoryKey === key
		const selectedCategory = transaction.category
		const analyticsClass = effectiveAnalyticsClass(transaction.analyticsClass)
		const excludedFromAnalytics = isExcludedFromOrdinaryAnalytics(analyticsClass)
		const availableSubcategories = selectedCategory === undefined ? [] : categorySubcategoryIds[selectedCategory] || []
		if (!isEditing) {
			return (
				<button type="button" className="category-cell-button" onClick={() => setEditingCategoryKey(key)} title="Editar categoría">
					{excludedFromAnalytics && (
						<span
							className={`analytics-class-flag ${analyticsClass}`}
							title={`${analyticsClassLabels[analyticsClass]}: excluido del análisis ordinario`}
							aria-label={`${analyticsClassLabels[analyticsClass]}, excluido del análisis ordinario`}
						>
							{analyticsClassSymbols[analyticsClass as Exclude<TransactionAnalyticsClass, 'normal'>]}
						</span>
					)}
					{!excludedFromAnalytics && transaction.suggestedAnalyticsClass && (
						<span
							className="analytics-suggestion-flag"
							title={`${analyticsClassLabels[transaction.suggestedAnalyticsClass]} sugerido: ${transaction.suggestionReason || 'movimiento inusual'}`}
							aria-label={`${analyticsClassLabels[transaction.suggestedAnalyticsClass]} sugerido`}
						>
							?
						</span>
					)}
					<span>{transactionCategoryLabel(transaction)}</span>
				</button>
			)
		}
		return (
			<div className="category-editor">
				<select
					value={analyticsClass}
					disabled={savingCategoryKey === key}
					onChange={event => updateTransactionAnalyticsClass(transaction, event.target.value as TransactionAnalyticsClass)}
					aria-label="Clase de análisis"
				>
					{(Object.keys(analyticsClassLabels) as TransactionAnalyticsClass[]).map(classId => (
						<option key={classId} value={classId}>{analyticsClassLabels[classId]}</option>
					))}
				</select>
				{transaction.suggestedAnalyticsClass && transaction.analyticsClassSource !== 'manual' && (
					<div className="analytics-suggestion">
						<span>
							Sugerido: {analyticsClassLabels[transaction.suggestedAnalyticsClass]}
							{transaction.suggestionConfidence !== undefined ? ` · ${Math.round(transaction.suggestionConfidence * 100)}%` : ''}
							{transaction.suggestionReason ? ` · ${transaction.suggestionReason}` : ''}
						</span>
						<button
							type="button"
							onClick={() => updateTransactionAnalyticsClass(transaction, 'normal')}
							disabled={savingCategoryKey === key}
							title="Mantener como ordinario"
							aria-label="Mantener como ordinario"
						>
							✓
						</button>
					</div>
				)}
				<select
					value={selectedCategory === undefined ? '' : String(selectedCategory)}
					disabled={savingCategoryKey === key}
					onChange={event => {
						const nextCategory = event.target.value === '' ? undefined : Number(event.target.value)
						updateTransactionCategory(transaction, nextCategory, undefined)
					}}
					aria-label="Categoría"
				>
					<option value="">Sin categoría</option>
					{sortedCategoryIds().map(categoryId => (
						<option key={categoryId} value={categoryId}>{categoryLabel(categoryId)}</option>
					))}
				</select>
				<select
					value={transaction.subcategory === undefined ? '' : String(transaction.subcategory)}
					disabled={savingCategoryKey === key || selectedCategory === undefined || availableSubcategories.length === 0}
					onChange={event => {
						const nextSubcategory = event.target.value === '' ? undefined : Number(event.target.value)
						updateTransactionCategory(transaction, selectedCategory, nextSubcategory)
					}}
					aria-label="Subcategoría"
				>
					<option value="">Sin subcategoría</option>
					{sortedSubcategoryIds(availableSubcategories).map(subcategoryId => (
						<option key={subcategoryId} value={subcategoryId}>{subcategoryLabel(subcategoryId)}</option>
					))}
				</select>
			</div>
		)
	}

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	const startTransactionColumnResize = (columnIndex: number, event: ReactMouseEvent<HTMLSpanElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const startX = event.clientX
		const startWidth = transactionColumnWidths[columnIndex]
		const handleMouseMove = (moveEvent: MouseEvent) => {
			const nextWidth = Math.max(minimumTransactionColumnWidths[columnIndex], startWidth + moveEvent.clientX - startX)
			setTransactionColumnWidths(widths => widths.map((width, index) => index === columnIndex ? nextWidth : width))
		}
		const handleMouseUp = () => {
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseup', handleMouseUp)
			document.body.classList.remove('resizing-transaction-column')
		}
		document.body.classList.add('resizing-transaction-column')
		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseup', handleMouseUp)
	}

	const renderTransactionHeaderCell = (label: string, columnIndex: number) => (
		<div className="grid-cell resizable-header-cell">
			<span>{label}</span>
			<span
				className="column-resize-handle"
				onMouseDown={event => startTransactionColumnResize(columnIndex, event)}
				role="separator"
				aria-orientation="vertical"
				aria-label={`Cambiar ancho de ${label}`}
				tabIndex={-1}
			/>
		</div>
	)

	const renderTransactionHeader = () => (
		<div className="transactions-grid transaction-header-grid" style={transactionGridStyle}>
			<div className="grid-row" key="header">
				{renderTransactionHeaderCell('Fecha', 0)}
				{renderTransactionHeaderCell('Concepto', 1)}
				{renderTransactionHeaderCell('Descripción', 2)}
				{renderTransactionHeaderCell('Categoría', 3)}
				{renderTransactionHeaderCell('Importe', 4)}
				{renderTransactionHeaderCell('Saldo', 5)}
			</div>
		</div>
	)

	const setAppliedFilters = useCallback((filters: BankFilterState, historyMode: 'push' | 'replace' | 'none' = 'push') => {
		setSearch(filters.search)
		setDateFrom(filters.dateFrom)
		setDateUntil(filters.dateUntil)
		setAmountMin(filters.amountMin)
		setAmountMax(filters.amountMax)
		setTransactionType(filters.transactionType)
		setSorting(filters.sorting)
		setCategory(filters.category)
		setSubcategory(filters.subcategory)
		setCategoryDrilldown(filters.category || undefined)
		setDraftSearch(filters.search)
		setDraftDateFrom(filters.dateFrom)
		setDraftDateUntil(filters.dateUntil)
		setDraftAmountMin(filters.amountMin)
		setDraftAmountMax(filters.amountMax)
		setDraftTransactionType(filters.transactionType)
		setDraftSorting(filters.sorting)
		setDraftCategory(filters.category)
		setListCursor(undefined)
		setNextListCursor(undefined)
		if (historyMode !== 'none') {
			const nextUrl = bankFilterUrl(filters)
			const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
			if (nextUrl !== currentUrl) {
				if (historyMode === 'replace') {
					window.history.replaceState({}, '', nextUrl)
				} else {
					window.history.pushState({}, '', nextUrl)
				}
			}
		}
	}, [])

	const applyFilters = () => {
		setAppliedFilters({
			search: draftSearch,
			dateFrom: draftDateFrom,
			dateUntil: draftDateUntil,
			amountMin: draftAmountMin,
			amountMax: draftAmountMax,
			transactionType: draftTransactionType,
			sorting: draftSorting,
			category: draftCategory,
			subcategory: draftCategory === category ? subcategory : '',
		})
	}

	const clearFilters = () => {
		setAppliedFilters(defaultBankFilterState)
	}

	const saveCurrentFilters = () => {
		const name = savedFilterName.trim()
		if (!name) return
		const now = new Date().toISOString()
		const existingFilter = savedFilters.find(savedFilter => savedFilter.name.trim().toLocaleLowerCase('es-ES') === name.toLocaleLowerCase('es-ES'))
		const nextSavedFilter: SavedBankFilter = {
			id: existingFilter?.id || `${Date.now()}`,
			name,
			filters: currentAppliedFilters,
			createdAt: existingFilter?.createdAt || now,
			updatedAt: now,
		}
		const nextSavedFilters = existingFilter
			? savedFilters.map(savedFilter => savedFilter.id === existingFilter.id ? nextSavedFilter : savedFilter)
			: [...savedFilters, nextSavedFilter]
		const sortedSavedFilters = nextSavedFilters.sort((first, second) => first.name.localeCompare(second.name, 'es-ES'))
		setSavedFilters(sortedSavedFilters)
		writeSavedBankFilters(sortedSavedFilters)
		setSelectedSavedFilterId(nextSavedFilter.id)
		setSavedFilterName('')
	}

	const openSavedFilter = (savedFilterId: string) => {
		setSelectedSavedFilterId(savedFilterId)
		const savedFilter = savedFilters.find(filter => filter.id === savedFilterId)
		if (!savedFilter) return
		setAppliedFilters(savedFilter.filters)
	}

	const deleteSelectedSavedFilter = () => {
		if (!selectedSavedFilterId) return
		const nextSavedFilters = savedFilters.filter(savedFilter => savedFilter.id !== selectedSavedFilterId)
		setSavedFilters(nextSavedFilters)
		writeSavedBankFilters(nextSavedFilters)
		setSelectedSavedFilterId('')
	}

	const focusBalanceDiffTransactions = (index: number) => {
		const point = balancePoints[index]
		if (!point) return
		const previousPoint = balancePoints[index - 1]
		setAppliedFilters({
			search: '',
			dateFrom: previousPoint ? previousPoint.point.date : point.point.date,
			dateUntil: point.point.date,
			amountMin: '',
			amountMax: '',
			transactionType: 'anything',
			sorting: 'dateDesc',
			category: '',
			subcategory: '',
		})
	}

	const focusMonthlyFlowTransactions = (month: MonthlyFlow, kind: 'income' | 'expenses') => {
		const range = kind === 'income'
			? shiftedIncomeMonthRange(month.month)
			: {
				from: monthStartInput(month.month),
				until: monthEndInput(month.month),
			}
		setAppliedFilters({
			search: '',
			dateFrom: range.from,
			dateUntil: range.until,
			amountMin: '',
			amountMax: '',
			transactionType: kind === 'income' ? 'credit' : 'debit',
			sorting: 'dateDesc',
			category: '',
			subcategory: '',
		})
	}

	const focusCategoryTransactions = (nextCategory?: number) => {
		const nextCategoryValue = String(nextCategory ?? 'uncategorized')
		const chartDateFrom = formatDateInput(chartData.range?.lower)
		const chartDateUntil = formatDateInput(chartData.range?.upper)
		setAppliedFilters({
			search,
			dateFrom: chartDateFrom || dateFrom,
			dateUntil: chartDateUntil || dateUntil,
			amountMin,
			amountMax,
			transactionType: 'anything',
			sorting: 'dateDesc',
			category: nextCategoryValue,
			subcategory: '',
		})
	}

	const focusSubcategoryTransactions = (nextCategory?: number, nextSubcategory?: number) => {
		const chartDateFrom = formatDateInput(chartData.range?.lower)
		const chartDateUntil = formatDateInput(chartData.range?.upper)
		setAppliedFilters({
			search,
			dateFrom: chartDateFrom || dateFrom,
			dateUntil: chartDateUntil || dateUntil,
			amountMin,
			amountMax,
			transactionType: 'anything',
			sorting: 'dateDesc',
			category: String(nextCategory ?? 'uncategorized'),
			subcategory: nextSubcategory === undefined ? 'uncategorized' : String(nextSubcategory),
		})
	}

	const focusHistoricalCategoryTransactions = (nextCategory?: number, nextSubcategory?: number) => {
		setAppliedFilters({
			search: '',
			dateFrom: formatDateInput(bounds?.dates?.lower),
			dateUntil: formatDateInput(bounds?.dates?.upper),
			amountMin: '',
			amountMax: '',
			transactionType: 'anything',
			sorting: 'dateDesc',
			category: String(nextCategory ?? 'uncategorized'),
			subcategory: categoryDrilldown
				? nextSubcategory === undefined ? 'uncategorized' : String(nextSubcategory)
				: '',
		})
	}

	const resetCategoryChart = () => {
		setAppliedFilters({
			search,
			dateFrom,
			dateUntil,
			amountMin,
			amountMax,
			transactionType,
			sorting,
			category: '',
			subcategory: '',
		})
	}

	const categorizeTransactions = () => {
		if (!hasManualCategoryChanges || isCategorizingTransactions) return
		setIsCategorizingTransactions(true)
		apiFetch('/bank/categorize', {
			method: 'POST',
		})
			.then(response => {
				if (!response.ok) {
					throw new Error(`Categorization failed with status ${response.status}`)
				}
				return response.json()
			})
			.then(() => {
				setHasManualCategoryChanges(false)
				previousQuerySignature.current = undefined
				setListCursor(undefined)
				setNextListCursor(undefined)
				setTransactions([])
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error('Error categorizing transactions:', error)
			})
			.finally(() => {
				setIsCategorizingTransactions(false)
			})
	}

	const loadMoreAmountSection = (sectionId: AmountSectionId) => {
		const section = amountSectionPages.find(item => item.id === sectionId)
		if (!section?.nextCursor || !amountThresholds || loadingAmountSection) return
		const requestedQuerySignature = querySignature
		setLoadingAmountSection(sectionId)
		setListError('')
		const params = transactionListParams(currentAppliedFilters, section.nextCursor, sectionId, amountThresholds)
		apiFetch(`/bank?${params.toString()}`)
			.then(async response => {
				if (!response.ok) throw await responseError(response, 'No se pudieron cargar más movimientos')
				return response.json()
			})
			.then((list: AmountSectionsResponse) => {
				if (previousQuerySignature.current !== requestedQuerySignature) return
				const nextSection = list.amountSections.find(item => item.id === sectionId)
				if (!nextSection) throw new Error('La respuesta no incluye el segmento solicitado')
				setTransactions(previousTransactions => {
					const existingKeys = new Set(previousTransactions.map(getTransactionKey))
					const additional = nextSection.transactions.filter(transaction => !existingKeys.has(getTransactionKey(transaction)))
					return [...previousTransactions, ...additional]
				})
				setAmountSectionPages(previousSections => previousSections.map(previousSection => {
					if (previousSection.id !== sectionId) return previousSection
					const existingKeys = new Set(previousSection.transactionKeys)
					const additionalKeys = nextSection.transactions
						.map(getTransactionKey)
						.filter(key => !existingKeys.has(key))
						return {
							...previousSection,
							transactionKeys: [...previousSection.transactionKeys, ...additionalKeys],
							nextCursor: nextSection.nextCursor,
							totalCount: nextSection.totalCount ?? previousSection.totalCount,
							totalAmount: nextSection.totalAmount ?? previousSection.totalAmount,
						}
				}))
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error(`Error loading more ${sectionId} transactions:`, error)
				setListError(error instanceof Error ? error.message : 'No se pudieron cargar más movimientos')
			})
			.finally(() => {
				setLoadingAmountSection(undefined)
			})
	}

	const toggleAmountSection = (section: AmountSectionId) => {
		setCollapsedAmountSections(prev => (
			prev.includes(section)
				? prev.filter(collapsed => collapsed !== section)
				: [...prev, section]
		))
	}

	const closeDateGapsModal = () => {
		setShowDateGaps(false)
		if (!hasDateGapsModalImport) return
		setHasDateGapsModalImport(false)
		previousQuerySignature.current = undefined
		setListCursor(undefined)
		setNextListCursor(undefined)
		setTransactions([])
		setDataRefreshVersion(version => version + 1)
	}

	const submitAuthName = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		const name = authNameInput.trim()
		if (!name) return
		saveBankAuthorizationName(name)
		setAuthName(name)
		setAuthNameSubmitted(true)
		if (currentAuthResult) {
			const url = new URL(window.location.href)
			url.searchParams.delete('auth')
			url.searchParams.delete('authResult')
			window.history.replaceState({}, '', url.toString())
			window.location.href = url.toString()
		}
	}

	useEffect(() => {
		const updateHeaderState = () => {
			const scrollTop = document.documentElement.scrollTop || document.body.scrollTop || window.pageYOffset
			setIsHeaderCollapsed(scrollTop > 72)
		}
		updateHeaderState()
		window.addEventListener('scroll', updateHeaderState, { passive: true })
		return () => {
			window.removeEventListener('scroll', updateHeaderState)
		}
	}, [])

	useEffect(() => {
		const restoreFiltersFromHistory = () => {
			setAppliedFilters(parseBankFilterState(), 'none')
		}
		window.addEventListener('popstate', restoreFiltersFromHistory)
		return () => {
			window.removeEventListener('popstate', restoreFiltersFromHistory)
		}
	}, [setAppliedFilters])

	useEffect(() => {
		if (shouldShowNameForm || shouldShowAuthorizationProgress) return
		apiFetch('/bank/date-gaps')
			.then(response => response.json())
			.then(list => {
				setEmptyDateRanges((list.gaps || []).map((gap: EmptyDateRange) => ({
					...gap,
					from: new Date(gap.from),
					until: new Date(gap.until),
				})))
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error("Error fetching date gaps:", error)
			})
	}, [dataRefreshVersion, shouldShowAuthorizationProgress, shouldShowNameForm])

	useEffect(() => {
		if (shouldShowNameForm || shouldShowAuthorizationProgress) return
		apiFetch('/bank/summary')
			.then(response => response.json())
			.then(summary => {
				setDashboardSummary(summary || {})
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error("Error fetching bank dashboard summary:", error)
			})
	}, [dataRefreshVersion, shouldShowAuthorizationProgress, shouldShowNameForm])

	useEffect(() => {
		if (shouldShowNameForm || shouldShowAuthorizationProgress) return
		let isStale = false
		setBalanceTooltip(undefined)
		setAverageBalanceTooltip(undefined)
		setFlowTooltip(undefined)
		setCategoryTooltip(undefined)
		setIsChartLoading(true)
		const params = new URLSearchParams({ period: chartPeriod })
		if (search.trim()) {
			params.append('search', search.trim())
		}
		if (dateFrom) {
			params.append('from', startOfInputDay(dateFrom))
		} else if (dateUntil && dateLowerInput) {
			params.append('from', startOfInputDay(dateLowerInput))
		}
		if (dateUntil) {
			params.append('until', endOfInputDay(dateUntil))
		} else if (dateFrom && dateUpperInput) {
			params.append('until', endOfInputDay(dateUpperInput))
		}
		if (amountMin !== '') {
			params.append('min', amountMin)
		}
		if (amountMax !== '') {
			params.append('max', amountMax)
		}
		if (transactionType === 'debit') {
			params.append('debits', 'true')
		} else if (transactionType === 'credit') {
			params.append('credits', 'true')
		}
		if (category) {
			params.append('category', category)
		}
		if (subcategory) {
			params.append('subcategory', subcategory)
		}
		appendAnalyticsChartFilter(params, includeNonNormalAnalytics)
		apiFetch(`/bank/charts?${params.toString()}`)
			.then(response => response.json())
			.then(charts => {
				if (isStale) return
				setChartData({
					range: charts.range,
					balance: charts.balance || [],
					balanceAverage: charts.balanceAverage || [],
					monthlyFlow: charts.monthlyFlow || [],
					categoryBreakdown: charts.categoryBreakdown || [],
				})
				setIsChartLoading(false)
			})
			.catch(error => {
				if (isStale) return
				recoverApiAuthorization(error)
				console.error("Error fetching bank charts:", error)
				setIsChartLoading(false)
			})
		return () => {
			isStale = true
		}
	}, [amountMax, amountMin, category, chartPeriod, dataRefreshVersion, dateFrom, dateLowerInput, dateUntil, dateUpperInput, includeNonNormalAnalytics, search, shouldShowAuthorizationProgress, shouldShowNameForm, subcategory, transactionType])

	useEffect(() => {
		if (shouldShowNameForm || shouldShowAuthorizationProgress) return
		const isNewQuery = previousQuerySignature.current !== querySignature
		previousQuerySignature.current = querySignature
		const cursor = isNewQuery ? undefined : listCursor

		if (isNewQuery) {
			setTransactions([])
			setNextListCursor(undefined)
			setAmountSectionPages([])
			setAmountThresholds(undefined)
			setListError('')
		}

		setIsLoading(true)
		const params = transactionListParams({
			search,
			dateFrom,
			dateUntil,
			amountMin,
			amountMax,
			transactionType,
			sorting,
			category,
			subcategory,
		}, cursor)
		const query = params.toString()
		apiFetch(`/bank${query ? `?${query}` : ''}`)
			.then(async response => {
				if (!response.ok) throw await responseError(response, 'No se pudieron cargar los movimientos')
				return response.json()
			})
			.then(list => {
				if (list.bounds) {
					setBounds(list.bounds)
				}
				if (sorting === 'amount') {
					const amountList = list as AmountSectionsResponse
					const fetchedTransactions = amountList.amountSections.flatMap(section => section.transactions)
					setTransactions(fetchedTransactions)
					setAmountThresholds(amountList.amountThresholds)
						setAmountSectionPages(amountList.amountSections.map(section => ({
							...section,
							title: amountSectionTitles[section.id],
							transactionKeys: section.transactions.map(getTransactionKey),
							totalCount: section.totalCount || 0,
							totalAmount: section.totalAmount || 0,
						})))
					setNextListCursor(undefined)
					setListError('')
					setIsLoading(false)
					return
				}
				const fetchedTransactions = Array.isArray(list.transactions) ? list.transactions : []
				setTransactions(prev => {
					const baseTransactions = isNewQuery ? [] : prev
					const existingTransactionKeys = new Set(baseTransactions.map(getTransactionKey))
					const additional = fetchedTransactions.filter((transaction: Transaction) => !existingTransactionKeys.has(getTransactionKey(transaction)))
					return [...baseTransactions, ...additional].sort((a, b) => {
						const dateSort = sorting === 'dateAsc'
							? new Date(a.date).getTime() - new Date(b.date).getTime()
							: new Date(b.date).getTime() - new Date(a.date).getTime()
						if (dateSort !== 0) return dateSort
						return sorting === 'dateAsc' ? a.order - b.order : b.order - a.order
					})
				})
				setNextListCursor(list.nextCursor)
				setListError('')
				setIsLoading(false)
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error("Error fetching transactions:", error)
				setListError(error instanceof Error ? error.message : 'No se pudieron cargar los movimientos')
				setIsLoading(false)
			})
	}, [amountMax, amountMin, category, dataRefreshVersion, dateFrom, dateUntil, listCursor, querySignature, search, shouldShowAuthorizationProgress, shouldShowNameForm, sorting, subcategory, transactionType])

	useEffect(() => {
		if (!shouldShowAuthorizationProgress) return
		apiFetch('/bank/date-gaps')
			.catch(error => {
				recoverApiAuthorization(error)
				console.error("Error checking bank authorization:", error)
			})
	}, [shouldShowAuthorizationProgress])

	useEffect(() => {
		if (sorting === 'amount') return
		const observer = new IntersectionObserver(entries => {
			if (entries[0].isIntersecting && !isLoading && nextListCursor) {
				setListCursor(nextListCursor)
			}
		}, {
			threshold: 0,
			rootMargin: '0px 0px 100px 0px'
		})
		const currentTarget = observerTarget.current		
		if (currentTarget)  observer.observe(currentTarget)
		return () => {
			if (currentTarget) observer.unobserve(currentTarget)
		}
	}, [isLoading, nextListCursor, sorting])

	useEffect(() => {
		if (!shouldShowMobileRetry) {
			setMobileQrCodeUrl('')
			setMobileAuthorizationExpired(false)
			return
		}
		let isCancelled = false
		let pollCount = 0
		let interval: ReturnType<typeof setInterval> | undefined
		setMobileAuthorizationExpired(false)
		requestBankMobileAuthorizationUrl(authNameInput.trim())
			.then(authorizeUrl => {
				if (isCancelled) return
				const mobileUrl = `${window.location.origin}/banca/mobile-auth?name=${encodeURIComponent(authNameInput.trim())}&authorize=${encodeURIComponent(authorizeUrl)}`
				QRCode.toDataURL(mobileUrl, {
					errorCorrectionLevel: 'M',
					margin: 1,
					width: 280,
				}).then(qrCodeUrl => {
					if (!isCancelled) {
						setMobileQrCodeUrl(qrCodeUrl)
					}
				}).catch(error => {
					console.error('Error creating mobile authorization QR code:', error)
				})
				interval = setInterval(() => {
					pollCount += 1
					pollBankAuthorizationStatus()
						.then(isAuthorized => {
							if (isCancelled) return
							if (isAuthorized) {
								if (interval) clearInterval(interval)
								const url = new URL(window.location.href)
								url.searchParams.delete('auth')
								url.searchParams.delete('authResult')
								window.location.href = url.toString()
								return
							}
							if (pollCount === 12) {
								setMobileAuthorizationExpired(true)
							}
						})
						.catch(error => {
							console.error('Error polling bank authorization status:', error)
						})
				}, 5000)
			})
			.catch(error => {
				console.error('Error creating mobile authorization URL:', error)
			})
		return () => {
			isCancelled = true
			if (interval) clearInterval(interval)
		}
	}, [authNameInput, shouldShowMobileRetry])
	
	if (shouldShowNameForm) {
		return (
			<main className="bank-auth-name-screen">
				<form className="bank-auth-name-form" onSubmit={submitAuthName}>
					<label htmlFor="bank-auth-name">Nombre</label>
					{authMessage && !shouldShowMobileRetry &&
						<p className="bank-auth-message">{authMessage}</p>
					}
					<div className="bank-auth-name-input-wrap">
						<input
							id="bank-auth-name"
							type="text"
							value={authNameInput}
							onChange={event => setAuthNameInput(event.target.value)}
							autoComplete="given-name"
							autoFocus
						/>
						<button type="submit" aria-label="Enviar">↵</button>
					</div>
					{shouldShowMobileRetry &&
						<div className="bank-auth-mobile-retry">
							{mobileQrCodeUrl &&
								<img src={mobileQrCodeUrl} alt="Codigo QR para verificar desde el movil" />
							}
							<p>
								No hemos podido verificarte en la red. Evita conexiones WiFi. Puedes leer el siguiente código QR para indentificarte mediante red móvil.
							</p>
							{mobileAuthorizationExpired &&
								<p className="bank-auth-message">No se ha completado la verificacion. Vuelve a intentarlo.</p>
							}
						</div>
					}
				</form>
			</main>
		)
	}

	if (shouldShowAuthorizationProgress) {
		return (
			<main className="bank-auth-name-screen">
				<div className="bank-auth-mobile-loading" aria-label="Cargando" />
			</main>
		)
	}

	return (
		<div className={`banca${isHeaderCollapsed ? ' header-collapsed' : ''}`}>
			<div className="header">
				<div>
					<span className="eyebrow">Banca</span>
					<h2>Family money</h2>
				</div>
				<div className="header-actions">
					<button
						type="button"
						className="categorize-transactions-button"
						onClick={categorizeTransactions}
						disabled={!hasManualCategoryChanges || isCategorizingTransactions}
						aria-label="Autocategorizar movimientos"
						title="Autocategorizar movimientos"
					>
						<span>Auto</span>
						<strong>{isCategorizingTransactions ? '…' : '↻'}</strong>
						<span>{hasManualCategoryChanges ? 'Categorizar' : 'Sin cambios'}</span>
					</button>
					<button
						type="button"
						className={`gap-summary${showDateGaps ? ' active' : ''}`}
						onClick={() => setShowDateGaps(true)}
						aria-expanded={showDateGaps}
						aria-haspopup="dialog"
					>
						<span className="gap-summary-label">Huecos</span>
						<strong>{visibleMovementGaps.length.toLocaleString('es-ES')}</strong>
						<span>{longestGap ? `${longestGap.days.toLocaleString('es-ES')} días máx.` : 'Sin huecos'}</span>
					</button>
				</div>
			</div>
			{showDateGaps && (
				<div className="date-gaps-modal-backdrop" role="presentation">
					<section className="date-gaps-modal" role="dialog" aria-modal="true" aria-labelledby="date-gaps-title">
						<button type="button" className="modal-close-button" onClick={closeDateGapsModal} aria-label="Cerrar">
							×
						</button>
						<div className="section-heading">
							<div>
								<h3 id="date-gaps-title">Rangos sin movimientos</h3>
								<p>Comprueba huecos de importación antes de analizar tendencias.</p>
							</div>
							<label>
								<span>Mínimo</span>
								<input
									type="number"
									min="1"
									step="1"
									value={minimumGapDays}
									onChange={event => setMinimumGapDays(Math.max(1, Number(event.target.value) || 1))}
								/>
								<span>días</span>
							</label>
						</div>
						<BancFileUploader onImported={() => setHasDateGapsModalImport(true)} />
						<div className="empty-ranges-grid">
							<div className="grid-row" key="empty-ranges-header">
								<div className="grid-cell">Desde</div>
								<div className="grid-cell">Hasta</div>
								<div className="grid-cell">Días</div>
								<div className="grid-cell">Nota</div>
							</div>
							{visibleDateRanges.map((range, index) => (
								<div className="grid-row" key={`${range.from.toISOString()}-${range.until.toISOString()}-${index}`}>
									<div className="grid-cell">{isHistoryStart(range) ? formatDate(range.until) : formatDate(range.from)}</div>
									<div className="grid-cell">{isHistoryStart(range) ? '' : formatDate(range.until)}</div>
									<div className="grid-cell">{isHistoryStart(range) ? '' : range.days.toLocaleString('es-ES')}</div>
									<div className="grid-cell">{range.note}</div>
								</div>
							))}
						</div>
					</section>
				</div>
			)}
			{conceptModalConcept && (
				<div className="date-gaps-modal-backdrop" role="presentation">
					<section className="date-gaps-modal concept-modal" role="dialog" aria-modal="true" aria-labelledby="concept-modal-title">
						<button type="button" className="modal-close-button" onClick={closeConceptModal} aria-label="Cerrar">
							×
						</button>
						<div className="section-heading">
							<div>
								<h3 id="concept-modal-title">{conceptModalField === 'concept' ? 'Concepto' : 'Descripción'}</h3>
								<p>{conceptModalConcept}</p>
							</div>
						</div>
						{isConceptSummaryLoading ? (
							<div className="concept-modal-loading"><div className="spinner"></div></div>
						) : conceptSummary ? (
							<>
								<div className="concept-summary-grid">
									<div>
										<span>Movimientos</span>
										<strong>{conceptSummary.count.toLocaleString('es-ES')}</strong>
									</div>
									<div>
										<span>Ingresos</span>
										<strong>{formatMoney(conceptSummary.income)}</strong>
									</div>
									<div>
										<span>Gastos</span>
										<strong>{formatMoney(conceptSummary.expenses)}</strong>
									</div>
									<div>
										<span>Neto</span>
										<strong>{formatMoney(conceptSummary.net)}</strong>
									</div>
								</div>
								<div className="concept-modal-dates">
									<span>{conceptSummary.firstDate ? `Desde ${formatDate(new Date(conceptSummary.firstDate))}` : 'Sin fecha inicial'}</span>
									<span>{conceptSummary.lastDate ? `Hasta ${formatDate(new Date(conceptSummary.lastDate))}` : 'Sin fecha final'}</span>
								</div>
								<div className="concept-category-breakdown">
									{conceptSummary.categories.map(item => (
										<span key={`${item.category ?? 'uncategorized'}-${item.subcategory ?? 'uncategorized'}`}>
											{item.subcategory !== undefined ? subcategoryLabel(item.subcategory) : categoryLabel(item.category)} · {item.count.toLocaleString('es-ES')}
										</span>
									))}
								</div>
								<div className="concept-category-controls">
									<label>
										<span>Categoría</span>
										<select value={conceptCategory} onChange={event => {
											setConceptCategory(event.target.value)
											setConceptSubcategory('')
										}}>
											<option value="">Sin categoría</option>
											{sortedCategoryIds().map(categoryId => (
												<option key={categoryId} value={categoryId}>{categoryLabel(categoryId)}</option>
											))}
										</select>
									</label>
									<label>
										<span>Subcategoría</span>
										<select
											value={conceptSubcategory}
											disabled={conceptCategory === '' || (categorySubcategoryIds[Number(conceptCategory)] || []).length === 0}
											onChange={event => setConceptSubcategory(event.target.value)}
										>
											<option value="">Sin subcategoría</option>
											{sortedSubcategoryIds(categorySubcategoryIds[Number(conceptCategory)] || []).map(subcategoryId => (
												<option key={subcategoryId} value={subcategoryId}>{subcategoryLabel(subcategoryId)}</option>
											))}
										</select>
									</label>
									<button type="button" onClick={categorizeConcept} disabled={isConceptCategorizing}>
										{isConceptCategorizing ? 'Guardando...' : 'Categorizar todos'}
									</button>
								</div>
							</>
						) : (
							<div className="empty-ledger">No se pudo cargar el resumen</div>
						)}
					</section>
				</div>
			)}
			<section className="bank-controls">
				<div className="bank-filter-summary" aria-label="Filtros aplicados">
					<span>{appliedFilterSummary}</span>
					<button type="button" className="scroll-top-button" onClick={scrollToTop} aria-label="Volver arriba" title="Volver arriba">
						↑
					</button>
				</div>
				<label className="search-control">
					<span>Buscar</span>
					<input
						type="search"
						value={draftSearch}
						placeholder="Concepto o descripción"
						onChange={event => setDraftSearch(event.target.value)}
					/>
				</label>
				<label className="filter-select-control">
					<span>Tipo</span>
					<select value={draftTransactionType} onChange={event => setDraftTransactionType(event.target.value as TransactionTypeFilter)}>
						<option value="anything">Ambos</option>
						<option value="debit">Gastos</option>
						<option value="credit">Ingresos</option>
					</select>
				</label>
				<label className="filter-select-control">
					<span>Orden</span>
					<select value={draftSorting} onChange={event => setDraftSorting(event.target.value as TransactionSorting)}>
						<option value="dateDesc">Recientes</option>
						<option value="dateAsc">Antiguos</option>
						<option value="amount">Importe</option>
					</select>
				</label>
				<label className="filter-select-control">
					<span>Categoría</span>
					<select value={draftCategory} onChange={event => setDraftCategory(event.target.value)}>
						<option value="">Todas</option>
						<option value="uncategorized">Sin categoría</option>
						{categoryIds.map(categoryId => (
							<option key={categoryId} value={categoryId}>{categoryLabel(categoryId)}</option>
						))}
					</select>
				</label>
				<div className="saved-filter-control">
					<span>Búsquedas</span>
					<div className="saved-filter-row">
						<select value={selectedSavedFilterId} onChange={event => openSavedFilter(event.target.value)} aria-label="Abrir búsqueda guardada">
							<option value="">Abrir...</option>
							{savedFilters.map(savedFilter => (
								<option key={savedFilter.id} value={savedFilter.id}>{savedFilter.name}</option>
							))}
						</select>
						<input
							type="text"
							value={savedFilterName}
							placeholder="Nombre"
							onChange={event => setSavedFilterName(event.target.value)}
							onKeyDown={event => {
								if (event.key === 'Enter') {
									event.preventDefault()
									saveCurrentFilters()
								}
							}}
							aria-label="Nombre de búsqueda guardada"
						/>
						<button type="button" onClick={saveCurrentFilters} disabled={!savedFilterName.trim()} aria-label="Guardar búsqueda" title="Guardar búsqueda">
							+
						</button>
						<button type="button" onClick={deleteSelectedSavedFilter} disabled={!selectedSavedFilterId} aria-label="Eliminar búsqueda guardada" title="Eliminar búsqueda guardada">
							−
						</button>
					</div>
				</div>
				<div className="range-filter">
					<span className="range-filter-label">Fechas</span>
					<div
						className="double-range"
						style={{
							'--range-start': `${dateRangeStartPercent}%`,
							'--range-end': `${dateRangeEndPercent}%`,
						} as CSSProperties}
					>
						<div className="double-range-track" aria-hidden="true" />
						<input
							type="range"
							aria-label="Fecha mínima"
							min={dateLowerValue}
							max={dateUpperValue}
							step={24 * 60 * 60 * 1000}
							value={selectedDateFromValue}
							disabled={!hasDateBounds}
							onChange={event => updateDateRange(Number(event.target.value), selectedDateUntilValue)}
						/>
						<input
							type="range"
							aria-label="Fecha máxima"
							min={dateLowerValue}
							max={dateUpperValue}
							step={24 * 60 * 60 * 1000}
							value={selectedDateUntilValue}
							disabled={!hasDateBounds}
							onChange={event => updateDateRange(selectedDateFromValue, Number(event.target.value))}
						/>
					</div>
					<div className="range-values" aria-hidden="true">
						<span>{hasDateBounds ? formatDate(new Date(selectedDateFromValue)) : '-'}</span>
						<span>{hasDateBounds ? formatDate(new Date(selectedDateUntilValue)) : '-'}</span>
					</div>
				</div>
				<div className="range-filter">
					<span className="range-filter-label">Importe</span>
					<div
						className="double-range"
						style={{
							'--range-start': `${amountRangeStartPercent}%`,
							'--range-end': `${amountRangeEndPercent}%`,
						} as CSSProperties}
					>
						<div className="double-range-track" aria-hidden="true" />
						<input
							type="range"
							aria-label="Importe mínimo"
							min={amountLowerValue}
							max={amountUpperValue}
							step="1"
							value={selectedAmountMinValue}
							disabled={!bounds?.amounts}
							onChange={event => updateAmountRange(Number(event.target.value), selectedAmountMaxValue)}
						/>
						<input
							type="range"
							aria-label="Importe máximo"
							min={amountLowerValue}
							max={amountUpperValue}
							step="1"
							value={selectedAmountMaxValue}
							disabled={!bounds?.amounts}
							onChange={event => updateAmountRange(selectedAmountMinValue, Number(event.target.value))}
						/>
					</div>
					<div className="range-values" aria-hidden="true">
						<span>{formatMoney(selectedAmountMinValue)}</span>
						<span>{formatMoney(selectedAmountMaxValue)}</span>
					</div>
				</div>
				<div className="filter-action-buttons">
					<button type="button" className="update-ranges-button" onClick={applyFilters} disabled={!filtersChanged} aria-label="Aplicar filtros" title="Aplicar filtros">
						↻
					</button>
					<button type="button" className="clear-filters-button" onClick={clearFilters} disabled={!isFiltering && !filtersChanged} aria-label="Limpiar filtros" title="Limpiar filtros">
						×
					</button>
				</div>
			</section>
			{(transactions.length > 0 || isLoading || isFiltering) && (
				<>
					<section className="summary-grid">
						<div className="metric">
							<span className="metric-label">Saldo actual</span>
							<strong>{latestSummary ? formatMoney(latestSummary.balance) : '-'}</strong>
							<span className="metric-note">{latestSummary ? `Último movimiento ${formatDate(new Date(latestSummary.date))}` : 'Sin datos'}</span>
						</div>
						<div className="metric">
							<span className="metric-label">Saldo previsto</span>
							<strong className={metricClassName(forecast?.balance)}>{forecast ? formatMoney(forecast.balance) : '-'}</strong>
							<span className="metric-note">{forecast ? `Antes del próximo ingreso · ${formatDate(new Date(forecast.date))}` : 'Sin ingresos recurrentes'}</span>
						</div>
						<div className="metric">
							<span className="metric-label">Mes vs año anterior</span>
							<strong className={metricClassName(monthScore?.current)}>{formatSignedPercent(monthScore?.ratio)}</strong>
							<span className="metric-note">{monthScore ? `${formatSignedMoney(monthScore.current)} vs ${monthReferenceDateLabel} (${formatSignedMoney(monthScore.baseline)})` : 'Sin comparación'}</span>
						</div>
						<div className="metric">
							<span className="metric-label">Mes vs historial</span>
							<strong className={metricClassName(yearScore?.current)}>{formatSignedPercent(yearScore?.ratio)}</strong>
							<span className="metric-note">{yearScore && yearScore.sampleSize > 0 ? `${formatSignedMoney(yearScore.current)} vs media histórica a ${historyReferenceDateLabel} (${formatSignedMoney(yearScore.baseline)})` : 'Sin historial suficiente'}</span>
						</div>
					</section>

					<section className="insights-layout">
						<div className="chart-panel primary-chart">
							<div className="section-heading">
								<div>
									<h3>Evolución de saldo</h3>
									<p>{dailyBalancePoints.length > 0 ? `${chartRangeLabel} · todos los movimientos` : 'Sin movimientos para representar.'}</p>
								</div>
								<div className="chart-heading-controls">
									{renderChartPeriodControl()}
								</div>
							</div>
							<div className="chart-canvas">
								<svg className={`balance-chart${isChartLoading ? ' loading' : ''}`} viewBox={`0 0 ${balanceChartWidth} ${balanceChartHeight}`} role="img" aria-label="Evolución de saldo" onMouseLeave={() => { setBalanceTooltip(undefined); setAverageBalanceTooltip(undefined) }}>
									<g className="chart-grid">
										{[0, 1, 2, 3].map(index => {
											const y = balanceChartPadding.top + (index / 3) * balancePlotHeight
											return <line key={`balance-grid-${index}`} x1={balanceChartPadding.left} x2={balanceChartWidth - balanceChartPadding.right} y1={y} y2={y} />
										})}
									</g>
									<text className="chart-axis-label" x={balanceChartPadding.left} y={balanceChartPadding.top + 4}>{formatCompactMoney(balanceMax)}</text>
									<text className="chart-axis-label" x={balanceChartPadding.left} y={balanceChartHeight - balanceChartPadding.bottom}>{formatCompactMoney(balanceMin)}</text>
									{averageBalancePoints.length > 1 && (
										<g className="chart-average balance">
											<polyline points={averageBalancePath} />
											<text x={averageBalancePoints[averageBalancePoints.length - 1].x} y={averageBalancePoints[averageBalancePoints.length - 1].y - 5}>Media histórica</text>
										</g>
									)}
									{averageBalancePoints.slice(1).map((point, index) => {
										const previousPoint = averageBalancePoints[index]
										const pointIndex = index + 1
										return (
											<line
												key={`${point.point.date}-average-segment-hit-${index}`}
												className="average-balance-hit-area"
												x1={previousPoint.x}
												y1={previousPoint.y}
												x2={point.x}
												y2={point.y}
												onMouseEnter={event => updateAverageBalanceTooltip(pointIndex, event)}
												onMouseMove={event => updateAverageBalanceTooltip(pointIndex, event)}
											/>
										)
									})}
									{balancePoints.length > 1 && (
										<polyline className="balance-chart-line" points={balancePath} />
									)}
									{balancePoints.slice(1).map((point, index) => {
										const previousPoint = balancePoints[index]
										const pointIndex = index + 1
										return (
											<line
												key={`${point.point.date}-segment-hit-${index}`}
												className="balance-chart-segment-hit-area"
												x1={previousPoint.x}
												y1={previousPoint.y}
												x2={point.x}
												y2={point.y}
												onMouseEnter={event => updateBalanceTooltip(pointIndex, event)}
												onMouseMove={event => updateBalanceTooltip(pointIndex, event)}
												onClick={() => focusBalanceDiffTransactions(pointIndex)}
											/>
										)
									})}
									{balancePoints.map((point, index) => (
										<circle
											key={`${point.point.date}-${index}`}
											className={index === balancePoints.length - 1 ? 'balance-chart-point latest' : 'balance-chart-point'}
											cx={point.x}
											cy={point.y}
											r={index === balancePoints.length - 1 ? 4 : 2.5}
											onMouseEnter={event => updateBalanceTooltip(index, event)}
											onMouseMove={event => updateBalanceTooltip(index, event)}
											onFocus={() => setBalanceTooltip({ index, x: point.x, y: point.y })}
											onBlur={() => setBalanceTooltip(undefined)}
											onClick={() => focusBalanceDiffTransactions(index)}
											tabIndex={0}
										/>
									))}
									{balancePoints.map((point, index) => (
										<circle
											key={`${point.point.date}-hit-${index}`}
											className="balance-chart-hit-area"
											cx={point.x}
											cy={point.y}
											r="10"
											onMouseEnter={event => updateBalanceTooltip(index, event)}
											onMouseMove={event => updateBalanceTooltip(index, event)}
											onFocus={() => setBalanceTooltip({ index, x: point.x, y: point.y })}
											onBlur={() => setBalanceTooltip(undefined)}
											onClick={() => focusBalanceDiffTransactions(index)}
											tabIndex={0}
											aria-label={`${formatDate(new Date(point.point.date))}: ${formatMoney(point.point.balance)}`}
										/>
									))}
									{balancePoints.length > 0 && (
										<>
											{balanceMonthTicks.map(tick => (
												<text key={`balance-month-${tick.date}`} className="chart-date-label month" x={tick.x} y={balanceChartHeight - 10}>
													{tick.label}
												</text>
											))}
											<text className="chart-date-label" x={balanceChartPadding.left} y={balanceChartHeight - 10}>
												{formatDate(new Date(dailyBalancePoints[0].date))}
											</text>
											<text className="chart-date-label end" x={latestBalancePoint?.x ?? balanceChartWidth - balanceChartPadding.right} y={balanceChartHeight - 10}>
												{formatDate(new Date(dailyBalancePoints[dailyBalancePoints.length - 1].date))}
											</text>
										</>
									)}
									{hoveredBalancePoint && (
										<g className="balance-chart-tooltip" transform={`translate(${balanceTooltipX} ${balanceTooltipY})`} pointerEvents="none">
											<rect width={balanceTooltipWidth} height={balanceTooltipHeight} rx="6" />
											<text className="tooltip-date" x="12" y="24">{formatDate(new Date(hoveredBalancePoint.point.date))}</text>
											<text className="tooltip-value" x="12" y="50">{formatMoney(hoveredBalancePoint.point.balance)}</text>
											<text className={`tooltip-diff ${hoveredPreviousBalancePoint && hoveredBalancePoint.point.balance - hoveredPreviousBalancePoint.point.balance < 0 ? 'negative' : 'positive'}`} x="12" y="71">
												{hoveredPreviousBalancePoint ? `(${formatSignedMoney(hoveredBalancePoint.point.balance - hoveredPreviousBalancePoint.point.balance)})` : ''}
											</text>
										</g>
									)}
									{hoveredAverageBalancePoint && (
										<g className="balance-chart-tooltip average" transform={`translate(${averageBalanceTooltipX} ${averageBalanceTooltipY})`} pointerEvents="none">
											<rect width={averageBalanceTooltipWidth} height={averageBalanceTooltipHeight} rx="6" />
											{averageBalanceTooltipSamples.map((sample, index) => (
												<text key={`${sample.year}-${index}`} className="tooltip-sample" x="12" y={24 + index * 22}>
													{sample.year} · {formatMoney(sample.balance)}
												</text>
											))}
										</g>
									)}
								</svg>
								{isChartLoading && (
									<div className="chart-loading-overlay" aria-label="Actualizando gráfica">
										<div className="spinner"></div>
									</div>
								)}
							</div>
						</div>

						<div className="chart-panel">
							<div className="section-heading">
								<div>
									<h3>Flujo mensual</h3>
									<p>{monthlyFlow.length > 0 ? `${chartRangeLabel} · ${includeNonNormalAnalytics ? 'todos los movimientos' : 'gasto ordinario'}` : 'Sin meses para representar.'}</p>
								</div>
								<div className="chart-heading-controls">
									{renderAnalyticsClassToggle()}
									{renderChartPeriodControl()}
								</div>
							</div>
							<div className="chart-canvas">
							<svg className={`flow-chart${isChartLoading ? ' loading' : ''}`} viewBox={`0 0 ${flowChartWidth} ${flowChartHeight}`} role="img" aria-label="Flujo mensual" onMouseLeave={() => setFlowTooltip(undefined)}>
								<g className="chart-grid">
									{[0, 1, 2, 3].map(index => {
										const y = flowChartPadding.top + (index / 3) * flowPlotHeight
										return <line key={`flow-grid-${index}`} x1={flowChartPadding.left} x2={flowChartWidth - flowChartPadding.right} y1={y} y2={y} />
									})}
								</g>
								{monthlyFlow.length > 0 && (
									<>
										<g className="chart-average income">
											<line x1={flowChartPadding.left} x2={flowChartWidth - flowChartPadding.right} y1={averageIncomeY} y2={averageIncomeY} />
											<text x={flowChartWidth - flowChartPadding.right} y={averageIncomeY - 5}>Media ingresos</text>
										</g>
										<g className="chart-average expenses">
											<line x1={flowChartPadding.left} x2={flowChartWidth - flowChartPadding.right} y1={averageExpensesY} y2={averageExpensesY} />
											<text x={flowChartWidth - flowChartPadding.right} y={averageExpensesY + 12}>Media gastos</text>
										</g>
									</>
								)}
								{monthlyFlow.map((month, index) => {
									const groupWidth = flowPlotWidth / Math.max(monthlyFlow.length, 1)
									const barWidth = Math.min(28, groupWidth / 3.8)
									const pairGap = Math.min(7, Math.max(3, groupWidth / 18))
									const pairWidth = barWidth * 2 + pairGap
									const groupX = flowChartPadding.left + index * groupWidth + groupWidth / 2
									const incomeX = groupX - pairWidth / 2
									const expensesX = incomeX + barWidth + pairGap
									const incomeHeight = (month.income / maxFlowValue) * flowPlotHeight
									const expensesHeight = (month.expenses / maxFlowValue) * flowPlotHeight
									const baseline = flowChartPadding.top + flowPlotHeight
									return (
										<g key={month.month}>
											<rect
												className="flow-bar income"
												x={incomeX}
												y={baseline - incomeHeight}
												width={barWidth}
												height={incomeHeight}
												rx="3"
												onMouseEnter={event => updateFlowTooltip(month, 'income', month.income, event)}
												onMouseMove={event => updateFlowTooltip(month, 'income', month.income, event)}
												onFocus={() => setFlowTooltip({ month: month.month, label: month.label, kind: 'income', amount: month.income, x: incomeX, y: baseline - incomeHeight })}
												onBlur={() => setFlowTooltip(undefined)}
												onClick={() => focusMonthlyFlowTransactions(month, 'income')}
												tabIndex={0}
												aria-label={`${month.label} ingresos: ${formatMoney(month.income)}`}
											/>
											<rect
												className="flow-bar expenses"
												x={expensesX}
												y={baseline - expensesHeight}
												width={barWidth}
												height={expensesHeight}
												rx="3"
												onMouseEnter={event => updateFlowTooltip(month, 'expenses', month.expenses, event)}
												onMouseMove={event => updateFlowTooltip(month, 'expenses', month.expenses, event)}
												onFocus={() => setFlowTooltip({ month: month.month, label: month.label, kind: 'expenses', amount: month.expenses, x: expensesX, y: baseline - expensesHeight })}
												onBlur={() => setFlowTooltip(undefined)}
												onClick={() => focusMonthlyFlowTransactions(month, 'expenses')}
												tabIndex={0}
												aria-label={`${month.label} gastos: ${formatMoney(month.expenses)}`}
											/>
											<text className="chart-date-label month" x={groupX} y={flowChartHeight - flowChartPadding.bottom + 22}>{month.label}</text>
										</g>
									)
								})}
								<g className="chart-legend">
									<circle className="income" cx="48" cy={flowChartPadding.top - 18} r="4" />
									<text x="58" y={flowChartPadding.top - 15}>Ingresos</text>
									<circle className="expenses" cx="124" cy={flowChartPadding.top - 18} r="4" />
									<text x="134" y={flowChartPadding.top - 15}>Gastos</text>
								</g>
								{flowTooltip && (
									<g className="flow-chart-tooltip" transform={`translate(${flowTooltipX} ${flowTooltipY})`} pointerEvents="none">
										<rect width={flowTooltipWidth} height={flowTooltipHeight} rx="6" />
										<text className="tooltip-date" x="12" y="24">{flowTooltip.label} · {flowTooltip.kind === 'income' ? 'Ingresos' : 'Gastos'}</text>
										<text className={flowTooltip.kind === 'income' ? 'tooltip-value positive' : 'tooltip-value negative'} x="12" y="50">{formatMoney(flowTooltip.amount)}</text>
									</g>
								)}
							</svg>
								{isChartLoading && (
									<div className="chart-loading-overlay" aria-label="Actualizando gráfica">
										<div className="spinner"></div>
									</div>
								)}
							</div>
						</div>

						<div className="chart-panel">
							<div className="section-heading">
								<div className="chart-title-with-back">
									{categoryDrilldown && (
										<button type="button" className="chart-back-button" onClick={resetCategoryChart} aria-label="Volver a categorías" title="Volver a categorías">
											<span aria-hidden="true">↑</span>
										</button>
									)}
									<div>
										<h3>{categoryChartTitle}</h3>
										<p>{categoryChartItems.length > 0 ? `${chartRangeLabel} · neto · ${includeNonNormalAnalytics ? 'todos los movimientos' : 'gasto ordinario'}` : 'Sin categorías para representar.'}</p>
									</div>
								</div>
								<div className="chart-heading-controls">
									{renderAnalyticsClassToggle()}
									{renderChartPeriodControl()}
								</div>
							</div>
							<div className="chart-canvas">
								<svg className={`category-chart${isChartLoading ? ' loading' : ''}`} style={{ aspectRatio: `${categoryChartWidth} / ${categoryChartHeight}` }} viewBox={`0 0 ${categoryChartWidth} ${categoryChartHeight}`} role="img" aria-label="Categorías" onMouseLeave={() => setCategoryTooltip(undefined)}>
									{categoryChartTotal > 0 ? (
										<>
											{categoryBarItems.map((item, index) => {
												const rowY = categoryChartPadding.top + index * (categoryBarRowHeight + categoryBarGap)
												const currentShare = categoryChartTotal > 0 ? Math.abs(item.amount) / categoryChartTotal : 0
												const monthlyAmount = Math.abs(item.amount) / categoryPeriodMonths
												const historicalShare = item.historicalShare || 0
												const historicalAmount = item.historicalAmount || 0
												const historicalMarkerShare = categoryChartTotal > 0 ? historicalAmount / categoryChartTotal : 0
												const barWidth = Math.max((currentShare / categoryBarMaxShare) * categoryBarPlotWidth, 2)
												const historicalX = categoryChartPadding.left + (historicalMarkerShare / categoryBarMaxShare) * categoryBarPlotWidth
												const color = categoryBarColor(item, index)
												const focusX = categoryChartPadding.left + barWidth
												const focusY = rowY + categoryBarRowHeight / 2
												const tooltipItem = { ...item, currentShare, monthlyAmount, historicalShare, historicalAmount }
												return (
													<g
														key={`${item.category ?? 'uncategorized'}-${item.subcategory ?? 'category-bar'}`}
														className="category-bar-row"
														onMouseEnter={event => updateCategoryTooltip(tooltipItem, event)}
														onMouseMove={event => updateCategoryTooltip(tooltipItem, event)}
														onFocus={() => setCategoryTooltip({ label: item.label, amount: item.amount, currentShare, monthlyAmount, historicalShare, historicalAmount, x: focusX, y: focusY })}
														onBlur={() => setCategoryTooltip(undefined)}
														onClick={() => categoryDrilldown ? focusSubcategoryTransactions(item.category, item.subcategory) : focusCategoryTransactions(item.category)}
														tabIndex={0}
														role="button"
														aria-label={`${item.label}: ${formatSignedMoney(item.amount)}`}
													>
														<text className="category-bar-label" x={categoryChartPadding.left - 14} y={rowY + 16}>{item.label}</text>
														<rect className="category-bar-track" x={categoryChartPadding.left} y={rowY} width={categoryBarPlotWidth} height={categoryBarRowHeight} rx="5" />
														<rect className="category-bar-fill" x={categoryChartPadding.left} y={rowY} width={barWidth} height={categoryBarRowHeight} rx="5" fill={color} />
														<line
															className="category-bar-average-marker"
															x1={historicalX}
															x2={historicalX}
															y1={rowY - 3}
															y2={rowY + categoryBarRowHeight + 3}
															onClick={event => {
																event.stopPropagation()
																focusHistoricalCategoryTransactions(item.category, item.subcategory)
															}}
															onKeyDown={event => {
																if (event.key !== 'Enter' && event.key !== ' ') return
																event.preventDefault()
																event.stopPropagation()
																focusHistoricalCategoryTransactions(item.category, item.subcategory)
															}}
															tabIndex={0}
															role="button"
															aria-label={`Ver movimientos históricos de ${item.label}`}
														/>
														<text className="category-bar-monthly" x={categoryChartWidth - 106} y={rowY + 16}>{formatRoundedMoney(monthlyAmount)}/mes</text>
														<text className="category-bar-share" x={categoryChartWidth - 34} y={rowY + 16}>{formatPercent(currentShare)}</text>
													</g>
												)
											})}
											{categoryRestSummary && (
												<text className="category-rest-summary" x={categoryChartPadding.left} y={categoryRestY}>
													Resto: {categoryRestSummary}
												</text>
											)}
											{categoryTooltip && (
												<g className="category-chart-tooltip" transform={`translate(${categoryTooltipX} ${categoryTooltipY})`} pointerEvents="none">
													<rect width={categoryTooltipWidth} height={categoryTooltipHeight} rx="6" />
													<text className="tooltip-value" x="12" y="26">{formatRoundedMoney(categoryTooltip.monthlyAmount || 0)}/mes</text>
													<text className="tooltip-average" x="12" y="52">Histórico {formatRoundedMoney((categoryTooltip.historicalAmount || 0) / categoryPeriodMonths)}/mes</text>
												</g>
											)}
										</>
									) : (
										<text className="chart-empty-label" x={categoryChartWidth / 2} y={categoryChartHeight / 2}>Sin datos</text>
									)}
								</svg>
								{isChartLoading && (
									<div className="chart-loading-overlay" aria-label="Actualizando gráfica">
										<div className="spinner"></div>
									</div>
								)}
							</div>
						</div>
					</section>

					<section className="content-grid">
						<div className="ledger">
							<div className="section-heading">
								<div>
									<h3>Movimientos</h3>
									<p>Historial completo, incluidos movimientos no ordinarios.</p>
								</div>
							</div>
							{sorting === 'amount' ? (
								<>
									{renderTransactionHeader()}
									<div className="amount-sections">
										{amountSections
										.filter(section => section.transactions.length > 0 || section.id === 'income')
										.map(section => {
											const isCollapsed = collapsedAmountSections.includes(section.id)
											const sectionTotal = section.totalAmount
											const sectionDebitTotal = section.id === 'income' ? 0 : Math.abs(section.totalAmount)
											const sectionShare = amountDebitsAbs > 0 ? sectionDebitTotal / amountDebitsAbs : 0
											const incomeCoverage = amountCredits > 0 ? Math.min(amountDebitsAbs / amountCredits, 1) : 0
											const spendingRatio = amountCredits > 0 ? amountDebitsAbs / amountCredits : 0
											const spendingDelta = Math.abs(spendingRatio - 1)
											return (
												<div className="amount-section" key={section.id}>
													<button
														type="button"
														className="amount-section-header"
														onClick={() => toggleAmountSection(section.id)}
														aria-expanded={!isCollapsed}
													>
														<span className="amount-section-toggle">{isCollapsed ? '▾' : '▴'}</span>
														<span>{section.title}</span>
														<strong className={section.id === 'income' ? 'positive' : ''}>
															{section.id === 'income' ? `+${formatMoney(Math.abs(sectionTotal))}` : formatMoney(sectionDebitTotal)}
														</strong>
													</button>
														<div className="amount-section-context">
															<span>
																{section.id === 'income'
																	? `Gastos totales: ${formatMoney(amountDebitsAbs)}`
																	: `${section.transactions.length.toLocaleString('es-ES')} de ${section.totalCount.toLocaleString('es-ES')} movimientos: ${Math.round(sectionShare * 100).toLocaleString('es-ES')}%`}
															</span>
															<span>
																{section.id === 'income'
																	? `${amountCredits >= amountDebitsAbs ? 'Gastado' : 'Sobregasto'} ${spendingDelta.toLocaleString('es-ES', { style: 'percent', maximumFractionDigits: 2 })}`
																	: formatMoney(amountDebitsAbs)}
															</span>
													</div>
													<div className={`amount-section-bar ${section.id}`}>
														<span className="bar-base"></span>
														<span
															className="bar-debits"
															style={{ width: `${section.id === 'income' ? incomeCoverage * 100 : Math.min(sectionShare * 100, 100)}%` }}
														></span>
													</div>
														{!isCollapsed && section.transactions.length > 0 && (
															<>
																<div className="transactions-grid amount-transactions" style={transactionGridStyle}>
																	{section.transactions.map(transaction => (
																		<div
																			key={getTransactionKey(transaction)}
																			className={`grid-row ${transaction.amount > 0 ? 'green' : (transaction.balance < 0 ? 'red' : '')}`}
																		>
																			<div className="grid-cell">
																				{formatDate(new Date(transaction.date))}
																			</div>
																			<div className="grid-cell">
																				{renderConceptCell(transaction)}
																			</div>
																			<div className="grid-cell">
																				{renderDescriptionCell(transaction)}
																			</div>
																			<div className="grid-cell category-cell">
																				{renderCategoryCell(transaction)}
																			</div>
																			<div className="grid-cell money">
																				{formatMoney(transaction.amount)}
																			</div>
																			<div className="grid-cell money">
																				{formatMoney(transaction.balance)}
																			</div>
																		</div>
																	))}
																</div>
																{section.nextCursor && (
																	<button
																		type="button"
																		className="amount-section-load-more"
																		onClick={() => loadMoreAmountSection(section.id)}
																		disabled={loadingAmountSection !== undefined}
																	>
																		{loadingAmountSection === section.id ? 'Cargando…' : 'Cargar más'}
																	</button>
																)}
															</>
														)}
														{!isCollapsed && section.transactions.length === 0 && (
															<div className="amount-section-empty">No hay movimientos en este segmento</div>
														)}
												</div>
											)
										})}
									</div>
								</>
							) : (
								<div className="transactions-grid" style={transactionGridStyle}>
									<div className="grid-row" key="header">{renderTransactionHeaderCell('Fecha', 0)}{renderTransactionHeaderCell('Concepto', 1)}{renderTransactionHeaderCell('Descripción', 2)}{renderTransactionHeaderCell('Categoría', 3)}{renderTransactionHeaderCell('Importe', 4)}{renderTransactionHeaderCell('Saldo', 5)}</div>
									{transactions.map(transaction => (
										<div
											key={getTransactionKey(transaction)}
											className={`grid-row ${transaction.amount > 0 ? 'green' : (transaction.balance < 0 ? 'red' : '')}`}
										>
											<div className="grid-cell">
												{formatDate(new Date(transaction.date))}
											</div>
											<div className="grid-cell">
												{renderConceptCell(transaction)}
											</div>
											<div className="grid-cell">
												{renderDescriptionCell(transaction)}
											</div>
											<div className="grid-cell category-cell">
												{renderCategoryCell(transaction)}
											</div>
											<div className="grid-cell money">
												{formatMoney(transaction.amount)}
											</div>
											<div className="grid-cell money">
												{formatMoney(transaction.balance)}
											</div>
										</div>
									))}
								</div>
							)}
								{listError && (
									<div className="ledger-error">{listError}</div>
								)}
								{!listError && !isLoading && transactions.length === 0 && (
									<div className="empty-ledger">
										No hay movimientos para la búsqueda actual
									</div>
								)}
								<div ref={observerTarget} className="ledger-sentinel">
									{isLoading && <span>Cargando movimientos...</span>}
								</div>
						</div>
					</section>
				</>
			)}
		</div>
	)
}

export default Banca
